import { aiDraftsHi } from './ai-drafts/hi';
import { automationsHi } from './automations/hi';
import { blockingHi } from './blocking/hi';
import { cachingHi } from './caching';
import { channelsHi } from './channels/hi';
import { chatWidgetHi } from './chat-widget/hi';
import { checksHi } from './checks';
import { commerceHi } from './commerce/hi';
import { decisionsHi } from './decisions';
import { developerHi } from './developer/hi';
import type { Translation } from './en';
import { offersHi } from './offers/hi';
import { privacyHi } from './privacy/hi';
import { reportsHi } from './reports/hi';
import { slaHi } from './sla/hi';
import { ticketsHi } from './tickets/hi';
import { toolsHi } from './tools';
import { chatSettingsHi } from './widget/hi';

/** Hindi. Pending review by native speakers, as the note in the interface says. */
export const hi: Translation = {
  app: {
    name: 'Hamnivo Studio',
    skipToContent: 'मुख्य सामग्री पर जाएं',
    language: 'भाषा',
    translationNote: 'इस अनुवाद की समीक्षा अभी मूल भाषी लोगों द्वारा होनी बाकी है।',
    close: 'बंद करें',
    cancel: 'रद्द करें',
    retry: 'फिर से कोशिश करें',
    reference: 'संदर्भ',
    copied: 'कॉपी हो गया',
    optional: 'वैकल्पिक',
    serverDetail: 'सर्वर से: {{detail}}',
    cancelled: 'रद्द किया गया। कुछ नहीं बदला।',
    loading: 'लोड हो रहा है',
  },
  nav: {
    label: 'Studio',
    home: 'होम',
    inbox: 'इनबॉक्स',
    ai: 'AI असिस्टेंट',
    sales: 'बिक्री',
    settings: 'सेटिंग',
    sections: '{{name}} के पेज',
    brands: 'ब्रांड और इंस्टॉल',
    agents: 'AI एजेंट',
    models: 'मॉडल कनेक्शन',
    knowledge: 'जानकारी',
    memory: 'ग्राहक मेमोरी',
    runs: 'गतिविधि',
    team: 'टीम और एक्सेस',
    security: 'सुरक्षा',
    platform: 'प्लेटफ़ॉर्म कंसोल',
    account: 'खाते की सुरक्षा',
    openMenu: 'मेन्यू खोलें',
    menu: 'मेन्यू',
    switchWorkspace: 'वर्कस्पेस बदलें',
    workspace: 'वर्कस्पेस',
    signOut: 'साइन आउट करें',
    signedInAs: 'इस खाते से साइन इन',
  },
  errors: {
    network: {
      title: 'Hamnivo से संपर्क नहीं हो सका',
      description: 'अपना कनेक्शन जांचें और फिर से कोशिश करें।',
    },
    denied: {
      title: 'आपको ऐसा करने की अनुमति नहीं है',
      description: 'वर्कस्पेस के मालिक या एडमिन से एक्सेस मांगें।',
    },
    notFound: {
      title: 'यह नहीं मिला',
      description: 'हो सकता है इसे हटा दिया गया हो, या लिंक गलत हो।',
    },
    validation: {
      title: 'कुछ जानकारी पर ध्यान देना होगा',
      description: 'विवरण जांचें और फिर से कोशिश करें।',
    },
    rateLimited: {
      title: 'बहुत ज़्यादा अनुरोध',
      description: 'थोड़ा रुकें, फिर से कोशिश करें।',
      retryIn: 'आप {{seconds}} सेकंड बाद फिर कोशिश कर सकते हैं।',
      retryNow: 'अब आप फिर कोशिश कर सकते हैं।',
    },
    unavailable: {
      title: 'कोई जुड़ी हुई सेवा जवाब नहीं दे रही',
      description: 'कुछ नहीं बदला। थोड़ी देर में फिर से कोशिश करें।',
    },
    idempotency: {
      title: 'यह पहले ही अलग विवरण के साथ भेजा जा चुका है',
      description: 'क्या सेव हुआ, यह देखने के लिए पेज फिर से लोड करें।',
    },
    conflict: {
      title: 'किसी और ने इसे पहले बदल दिया',
      description: 'नया वर्शन देखने के लिए फिर से लोड करें।',
    },
    mfa: {
      title: 'दो-चरणीय सत्यापन ज़रूरी है',
      description: 'खाते की सुरक्षा में दो-चरणीय सत्यापन चालू करें, फिर से कोशिश करें।',
    },
    tooLarge: { title: 'फ़ाइल बहुत बड़ी है', description: 'फ़ाइल 5 MB तक की हो सकती है।' },
    rejected: { title: 'फ़ाइल अस्वीकार की गई', description: 'फ़ाइल स्वीकार नहीं की जा सकी।' },
    disabled: {
      title: 'यह सुविधा बंद है',
      description: 'इसे चालू करने के लिए अपने एडमिन से कहें।',
    },
    internal: {
      title: 'हमारी तरफ़ से कुछ गड़बड़ हुई',
      description: 'कुछ नहीं बदला। फिर से कोशिश करें।',
    },
    planLimit: {
      title: 'प्लान की सीमा पूरी हो गई',
      description:
        'आपके प्लान में इससे ज़्यादा शामिल नहीं है। कुछ जगह ख़ाली करें, या वर्कस्पेस के मालिक से बड़े प्लान के बारे में पूछें।',
    },
    reauth: {
      title: 'जारी रखने के लिए फिर से साइन इन करें',
      description: 'आपकी सुरक्षा के लिए, इसके लिए हाल ही का साइन-इन ज़रूरी है।',
      action: 'फिर से साइन इन करें',
    },
    channelWindow: {
      title: 'अब सिर्फ़ टेम्पलेट संदेश भेजे जा सकते हैं',
      description:
        'WhatsApp ग्राहक के आख़िरी संदेश के बाद 24 घंटे तक ही जवाब देने देता है। कुछ नहीं भेजा गया। ग्राहक के दोबारा लिखने के बाद आप फिर जवाब दे सकते हैं।',
    },
    unknown: { title: 'कुछ गड़बड़ हुई', description: 'फिर से कोशिश करें।' },
    page: {
      title: 'यह पेज लोड नहीं हो सका',
      description: 'फिर से कोशिश करें। अगर ऐसा बार-बार हो, तो पेज फिर से लोड करें।',
    },
  },
  states: {
    stale: {
      since: 'इसे रीफ़्रेश नहीं किया जा सका। {{time}} लोड किया गया डेटा दिख रहा है।',
      unknown: 'इसे रीफ़्रेश नहीं किया जा सका। जो दिख रहा है वह पुराना हो सकता है।',
    },
    forbiddenTitle: 'आपके पास इस पेज का एक्सेस नहीं है',
    forbiddenDescription: 'वर्कस्पेस के मालिक या एडमिन से एक्सेस देने के लिए कहें।',
    backToOverview: 'सारांश पर वापस जाएं',
    notFoundTitle: 'पेज नहीं मिला',
    notFoundDescription: 'हो सकता है पता गलत हो, या पेज कहीं और चला गया हो।',
  },
  auth: {
    email: 'ईमेल पता',
    password: 'पासवर्ड',
    showPassword: 'पासवर्ड दिखाएं',
    hidePassword: 'पासवर्ड छिपाएं',
    errorSummary: 'आगे बढ़ने के लिए इन्हें ठीक करें',
    emailRequired: 'अपना ईमेल पता डालें।',
    emailInvalid: 'name@example.com जैसा ईमेल पता डालें।',
    passwordRequired: 'अपना पासवर्ड डालें।',
    rateLimited: 'बहुत ज़्यादा कोशिशें हुईं। कुछ मिनट रुकें, फिर से कोशिश करें।',
    privacy:
      'Studio एक सुरक्षित कुकी से आपको साइन इन रखता है। आप जो टाइप करते हैं, वह सेव करने तक इसी टैब में रहता है।',
    signIn: {
      title: 'फिर से स्वागत है',
      description: 'जारी रखने के लिए Hamnivo Studio में साइन इन करें।',
      keepSignedIn: 'इस डिवाइस पर मुझे साइन इन रखें',
      keepSignedInHint: 'साझा कंप्यूटर पर इसे बंद रखें। तब ब्राउज़र बंद होने पर आप साइन आउट हो जाएंगे।',
      submit: 'साइन इन करें',
      or: 'या',
      passkey: 'पासकी से साइन इन करें',
      forgot: 'पासवर्ड भूल गए?',
      noAccount: 'Hamnivo पर नए हैं?',
      createAccount: 'खाता बनाएं',
      failed: 'आपको साइन इन नहीं किया जा सका। अपना ईमेल और पासवर्ड जांचें, या पासवर्ड रीसेट करें।',
      notVerified: 'पहले अपने ईमेल पते की पुष्टि करें। साइन अप करते समय हमने आपको एक लिंक भेजा था।',
      resendVerification: 'लिंक फिर से भेजें',
      verificationSent: 'हमने नया लिंक भेजा है। अपना ईमेल देखें।',
      passkeyFailed: 'पासकी से साइन इन नहीं हो सका। फिर से कोशिश करें, या पासवर्ड से साइन इन करें।',
    },
    twoFactor: {
      title: 'पुष्टि करें कि यह आप ही हैं',
      description: 'अपने ऑथेंटिकेटर ऐप से 6 अंकों का कोड डालें।',
      code: '6 अंकों का कोड',
      codeHint: 'कोड हर 30 सेकंड में बदलता है।',
      submit: 'पुष्टि करें',
      useBackup: 'इसके बजाय बैकअप कोड इस्तेमाल करें',
      useApp: 'इसके बजाय ऐप का कोड इस्तेमाल करें',
      backupCode: 'बैकअप कोड',
      backupHint: 'दो-चरणीय सत्यापन चालू करते समय आपने जो कोड सेव किए थे, उनमें से एक।',
      codeRequired: 'कोड डालें।',
      invalid: 'यह कोड सही नहीं निकला। इसे जांचें और फिर से कोशिश करें।',
      expired: 'साइन इन की यह कोशिश खत्म हो गई। नई जांच के लिए फिर से साइन इन करें।',
      tooMany: 'बहुत ज़्यादा कोशिशें हुईं। जारी रखने के लिए फिर से साइन इन करें।',
      back: 'साइन इन पर वापस जाएं',
    },
    signUp: {
      title: 'अपना खाता बनाएं',
      description: 'इसके बाद आप एक वर्कस्पेस बना सकते हैं, या जिसमें आपको बुलाया गया है उसमें शामिल हो सकते हैं।',
      name: 'आपका नाम',
      nameRequired: 'अपना नाम डालें।',
      passwordHint: 'कम से कम 12 अक्षर। एक छोटा वाक्य याद रखना आसान होता है।',
      passwordShort: 'कम से कम 12 अक्षर इस्तेमाल करें।',
      passwordLong: 'ज़्यादा से ज़्यादा 256 अक्षर इस्तेमाल करें।',
      submit: 'खाता बनाएं',
      haveAccount: 'पहले से खाता है?',
      signIn: 'साइन इन करें',
      failed: 'यह खाता नहीं बनाया जा सका। अगर आपका पहले से खाता है, तो साइन इन करें या पासवर्ड रीसेट करें।',
    },
    checkEmail: {
      title: 'अपना ईमेल देखें',
      verify: 'हमने {{email}} पर एक लिंक भेजा है। अपने पते की पुष्टि करने और साइन अप पूरा करने के लिए उसे खोलें।',
      reset:
        'अगर कोई खाता {{email}} का इस्तेमाल करता है, तो हमने उस पर नया पासवर्ड चुनने का लिंक भेजा है। लिंक एक घंटे तक काम करता है।',
      help: 'नहीं मिल रहा? स्पैम फ़ोल्डर देखें, या एक मिनट रुककर फिर से भेजें।',
      resend: 'लिंक फिर से भेजें',
      sent: 'हमने नया लिंक भेजा है।',
      back: 'साइन इन पर वापस जाएं',
    },
    forgot: {
      title: 'अपना पासवर्ड रीसेट करें',
      description: 'अपने खाते का ईमेल पता डालें। हम नया पासवर्ड चुनने का लिंक भेजेंगे।',
      submit: 'रीसेट लिंक भेजें',
    },
    reset: {
      title: 'नया पासवर्ड चुनें',
      description: 'पासवर्ड बदलने पर आप अपने बाकी सेशन से साइन आउट हो जाएंगे।',
      password: 'नया पासवर्ड',
      submit: 'नया पासवर्ड सेव करें',
      invalidLink: 'यह रीसेट लिंक अमान्य है या इसकी समय-सीमा खत्म हो गई है। नया लिंक मांगें।',
      requestNew: 'नया लिंक मांगें',
      done: 'आपका पासवर्ड बदल गया। नए पासवर्ड से साइन इन करें।',
    },
    reauth: {
      stepUpTitle: 'पुष्टि करें कि यह आप ही हैं',
      stepUpDescription:
        'इस काम के लिए हाल ही में साइन इन होना ज़रूरी है। जारी रखने के लिए अपना पासवर्ड डालें। इस पेज पर आपका काम बना रहेगा।',
      expiredTitle: 'जारी रखने के लिए साइन इन करें',
      expiredDescription:
        'आपका सेशन खत्म हो गया। जारी रखने के लिए फिर से साइन इन करें। तब तक इस पेज पर बिना सेव किए बदलाव बने रहेंगे।',
      account: 'खाता',
      confirm: 'पुष्टि करें',
      signInAgain: 'फिर से साइन इन करें',
      otherAccount: 'किसी दूसरे खाते से साइन इन करें',
      masked: 'साइन इन करने तक वर्कस्पेस की सामग्री छिपी रहेगी।',
    },
  },
  tenants: {
    title: 'वर्कस्पेस चुनें',
    description:
      'वर्कस्पेस ब्रांड, लोगों और बातचीत को अलग-अलग रखते हैं। Studio इस ब्राउज़र में आपकी पसंद याद रखता है।',
    yours: 'आपके वर्कस्पेस',
    open: 'खोलें',
    current: 'मौजूदा',
    unavailable: 'आप अभी यह वर्कस्पेस नहीं खोल सकते।',
    status: {
      active: 'सक्रिय',
      suspended: 'निलंबित',
      closing: 'बंद हो रहा है',
      invited: 'न्योता बाकी है',
      removed: 'हटाया गया',
    },
    empty: {
      title: 'अभी कोई वर्कस्पेस नहीं',
      description: 'शुरू करने के लिए एक बनाएं, या किसी साथी से आपको बुलाने के लिए कहें।',
    },
    create: {
      title: 'वर्कस्पेस बनाएं',
      name: 'वर्कस्पेस का नाम',
      nameHint: 'आमतौर पर आपकी कंपनी या टीम का नाम।',
      slug: 'वर्कस्पेस का पता',
      slugHint: '3 से 40 छोटे अंग्रेज़ी अक्षर, अंक या हाइफ़न। लिंक में इस्तेमाल होता है।',
      language: 'डिफ़ॉल्ट भाषा',
      submit: 'वर्कस्पेस बनाएं',
      nameRequired: 'वर्कस्पेस का नाम डालें।',
      slugInvalid:
        '3 से 40 छोटे अंग्रेज़ी अक्षर, अंक या हाइफ़न इस्तेमाल करें, जो किसी अक्षर या अंक से शुरू और खत्म हों।',
      slugTaken: 'यह पता पहले से लिया जा चुका है। कोई दूसरा आज़माएं।',
      created: 'वर्कस्पेस बन गया।',
    },
    revoked: {
      title: 'इस वर्कस्पेस में आपका एक्सेस बदल गया है',
      description: 'अब आप {{name}} नहीं खोल सकते। इसका डेटा इस टैब से हटा दिया गया है।',
    },
  },
  shell: {
    aiStopped: 'AI रुका हुआ है',
    stopAi: 'AI रोकें',
    stopAiLabel: 'AI के जवाब रोकें',
    killBanner: {
      title: 'AI के जवाब रुके हुए हैं',
      people: 'लोग बातचीत जारी रखते हैं। नए संदेश आपकी टीम के पास जाते हैं।',
      review: 'रोक देखें',
    },
    scope: {
      tenant: 'इस वर्कस्पेस में हर जगह',
      brand: 'ब्रांड: {{name}}',
      agent: 'एजेंट: {{name}}',
    },
    reason: 'कारण: {{reason}}',
    since: '{{time}} से',
  },
  overview: {
    degraded: {
      title: 'होम का एक हिस्सा लोड नहीं हो सका',
      description:
        'Studio का बाक़ी हिस्सा काम कर रहा है, आपका इनबॉक्स भी। नीचे हर हिस्से को अलग से फिर आज़माया जा सकता है।',
    },
    title: 'सारांश',
    greeting: 'फिर से स्वागत है, {{name}}।',
    chats: {
      title: 'आपकी चैट',
      description: 'किसी व्यक्ति का इंतज़ार कर रहे ग्राहक, और आपको सौंपी गई चैट।',
      loading: 'आपकी चैट लोड हो रही हैं',
      waiting: 'किसी व्यक्ति का इंतज़ार',
      mine: 'आपको सौंपी गई',
      caughtUp: 'अभी आपके लिए कुछ भी इंतज़ार नहीं कर रहा।',
      open: 'इनबॉक्स खोलें',
    },
    setup: {
      title: 'शुरुआती सेटअप',
      description: 'ग्राहक आपके AI असिस्टेंट से चैट कर सकें, इससे पहले ये कदम बाकी हैं।',
      steps: {
        engine: {
          title: 'चैट इंजन जोड़ें',
          description: 'एक ब्रांड को जोड़ें, ताकि उसकी चैट आपकी टीम तक पहुँचें।',
        },
        brand: {
          title: 'ब्रांड जोड़ें और चैट इंस्टॉल करें',
          description: 'उसे नाम दें, अपनी वेबसाइट को अनुमति दें और उसमें इंस्टॉल कोड जोड़ें।',
        },
        agent: {
          title: 'AI एजेंट प्रकाशित करें',
          description: 'आपका असिस्टेंट ग्राहकों को तभी जवाब देता है जब आप उसे प्रकाशित करते हैं।',
        },
        team: {
          title: 'किसी साथी को आमंत्रित करें',
          description: 'उन लोगों को जोड़ें जो आपके साथ चैट का जवाब देंगे।',
        },
      },
    },
    brands: {
      title: 'ब्रांड',
      description: 'हर ब्रांड के लिए चैट कनेक्शन और AI की स्थिति।',
      loading: 'ब्रांड लोड हो रहे हैं',
      columns: { chat: 'चैट कनेक्शन', ai: 'AI असिस्टेंट' },
      install: 'इंस्टॉल',
      openAgent: 'एजेंट खोलें',
      empty: {
        title: 'अभी कोई ब्रांड नहीं',
        description: 'हर ब्रांड का अपना चैट विजेट, अनुमति वाली वेबसाइटें और AI असिस्टेंट होता है।',
        action: 'ब्रांड बनाएं',
      },
    },
    stops: {
      title: 'AI रोकने के स्विच',
      none: 'AI कहीं भी रुका नहीं है।',
      loading: 'रोकने के स्विच लोड हो रहे हैं',
      manage: 'रोक प्रबंधित करें',
    },
    runs: {
      title: 'हाल के रन',
      description: 'AI ने हाल में क्या किया। यहां किसी संदेश का टेक्स्ट नहीं रखा जाता।',
      loading: 'रन लोड हो रहे हैं',
      viewAll: 'सभी रन देखें',
      empty: {
        title: 'अभी कोई रन नहीं',
        description: 'जब AI किसी बातचीत में जवाब देता है या ड्राफ़्ट लिखता है, तब रन दिखते हैं।',
      },
    },
  },
  status: {
    connection: {
      active: 'जुड़ा हुआ',
      provisioning: 'सेट अप हो रहा है',
      degraded: 'ध्यान देने की ज़रूरत',
      disabled: 'बंद',
      none: 'जुड़ा नहीं',
    },
    agent: {
      live: 'लाइव · v{{version}}',
      draftOnly: 'सिर्फ़ ड्राफ़्ट',
      draftAhead: 'ड्राफ़्ट v{{version}} लाइव नहीं',
      archived: 'आर्काइव किया गया',
      none: 'कोई एजेंट नहीं',
    },
  },
  brands: {
    title: 'ब्रांड और इंस्टॉल',
    description: 'ब्रांड बनाएं, उसे Chatwoot से जोड़ें और अपनी वेबसाइट पर चैट जोड़ें।',
    loading: 'ब्रांड लोड हो रहे हैं',
    create: 'ब्रांड बनाएं',
    createTitle: 'ब्रांड बनाएं',
    name: 'ब्रांड का नाम',
    nameHint: 'ग्राहक इसे चैट में देखते हैं।',
    nameRequired: 'ब्रांड का नाम डालें।',
    websites: 'अनुमति वाली वेबसाइटें',
    websitesHint: 'हर लाइन में एक, जैसे https://shop.example.com। सिर्फ़ ये वेबसाइटें चैट लोड कर सकती हैं।',
    websiteInvalid:
      '{{value}} वेबसाइट का पता नहीं है। https://shop.example.com जैसा रूप इस्तेमाल करें, बिना पाथ के।',
    area: 'वर्कस्पेस का हिस्सा',
    submit: 'ब्रांड बनाएं',
    created: '{{name}} बन गया।',
    widgetId: 'विजेट ID',
    allowed: 'अनुमति वाली वेबसाइटें',
    noWebsites: 'अभी किसी वेबसाइट की अनुमति नहीं है, इसलिए चैट कहीं लोड नहीं होगी।',
    edit: 'बदलें',
    editLabel: '{{name}} बदलें',
    editTitle: '{{name}} बदलें',
    save: 'बदलाव सहेजें',
    saved: '{{name}} सहेजा गया।',
    removedWarning: 'सहेजते ही सूची से हटाई गई वेबसाइटों पर खुली चैट बंद हो जाएँगी, और वहाँ चैट लोड नहीं होगी:',
    empty: {
      title: 'अभी कोई ब्रांड नहीं',
      description: 'हर ब्रांड का अपना चैट विजेट, अनुमति वाली वेबसाइटें और AI असिस्टेंट होता है।',
    },
    chat: {
      title: 'चैट कनेक्शन',
      account: 'Chatwoot खाता {{id}}',
      connect: 'Chatwoot जोड़ें',
      connectTitle: '{{name}} को Chatwoot से जोड़ें',
      engineNote:
        'Hamnivo, Chatwoot को अपने चैट इंजन की तरह इस्तेमाल करता है। आप और आपकी टीम Hamnivo Studio में काम करते हैं; आपको Chatwoot खोलने की ज़रूरत नहीं है।',
      connectIntro: 'Hamnivo इस ब्रांड के लिए एक Chatwoot खाता सेट अप करता है, जिसमें होंगे:',
      connectItem1: 'एक इंटीग्रेशन यूज़र और वेबसाइट चैट के लिए एक इनबॉक्स,',
      connectItem2: 'एक AI बॉट जो उस इनबॉक्स में जवाब देता है,',
      connectItem3: 'Hamnivo को वापस भेजे जाने वाले हस्ताक्षरित वेबहुक।',
      connectNote:
        'ब्रांड का AI असिस्टेंट ड्राफ़्ट के रूप में बनता है। जब तक आप उसे प्रकाशित नहीं करते, वह ग्राहकों को जवाब नहीं देता।',
      connectSubmit: 'जोड़ें',
      connected: '{{name}} Chatwoot से जुड़ गया है।',
      noPermission: 'Chatwoot जोड़ने के लिए आपको इंटीग्रेशन प्रबंधित करने की अनुमति चाहिए।',
      statusHidden: 'यह स्थिति सिर्फ़ वे लोग देखते हैं जो पूरे वर्कस्पेस के इंटीग्रेशन प्रबंधित करते हैं।',
    },
    install: {
      title: 'चैट इंस्टॉल करें',
      description: 'यह कोड अनुमति वाली वेबसाइट के हर पेज पर, बंद होने वाले </body> टैग से ठीक पहले जोड़ें।',
      copy: 'इंस्टॉल कोड कॉपी करें',
      copied: 'इंस्टॉल कोड कॉपी हो गया।',
      copyFailed: 'कॉपी नहीं हो सका। कोड चुनें और खुद कॉपी करें।',
      note: 'सिर्फ़ कोड कॉपी करने से कुछ नहीं जुड़ता। अनुमति वाली वेबसाइट पर कोड चलने के बाद चैट दिखती है।',
    },
  },
  agents: {
    title: 'AI एजेंट',
    description:
      'हर एजेंट एक ब्रांड के लिए जवाब देता है। बदलाव ड्राफ़्ट के रूप में सेव होते हैं और प्रकाशित करने पर ही लाइव होते हैं।',
    loading: 'एजेंट लोड हो रहे हैं',
    columns: {
      answers: 'कहाँ जवाब देता है',
      name: 'एजेंट',
      brand: 'ब्रांड',
      model: 'मॉडल',
      live: 'लाइव वर्शन',
    },
    isDefault: 'ब्रांड का डिफ़ॉल्ट',
    draft: 'v{{version}}',
    notPublished: 'प्रकाशित नहीं',
    open: '{{name}} खोलें',
    create: 'एजेंट बनाएं',
    empty: {
      title: 'अभी कोई AI एजेंट नहीं',
      description:
        'किसी ब्रांड को Chatwoot से जोड़ने पर उसका असिस्टेंट ड्राफ़्ट के रूप में बनता है। आप यहां भी एजेंट बना सकते हैं।',
    },
    draftModel: 'ड्राफ़्ट: {{model}}',
    noPurpose: 'अभी कोई उद्देश्य नहीं लिखा गया।',
    answers: {
      customers: '{{brand}} के ग्राहकों को वेब चैट में और ब्रांड के उन चैनलों पर जवाब देता है जहाँ AI चालू है।',
      assist: '{{brand}} की चैट पर आपकी टीम को जवाब सुझाता है। ग्राहकों को कभी नहीं लिखता।',
      notDefault:
        'किसी को जवाब नहीं देता: केवल {{brand}} का डिफ़ॉल्ट एजेंट जवाब देता है। इस्तेमाल के लिए इसे डिफ़ॉल्ट बनाएँ।',
      notLive: 'अभी किसी को जवाब नहीं देता: यह प्रकाशित नहीं हुआ है।',
      archived: 'संग्रहीत। किसी को जवाब नहीं देता।',
    },
    filters: {
      search: 'एजेंट खोजें',
      searchHint: 'नाम, दिखने वाले नाम, उद्देश्य, ब्रांड या मॉडल से।',
      brand: 'ब्रांड',
      allBrands: 'सभी ब्रांड',
      state: 'दिखाएँ',
      all: 'सभी एजेंट',
      answering: 'अभी जवाब दे रहे हैं',
      idle: 'जवाब नहीं दे रहे',
      count: '{{total}} में से {{shown}} एजेंट दिख रहे हैं।',
      none: 'इन फ़िल्टर से कोई एजेंट मेल नहीं खाता।',
      clear: 'फ़िल्टर हटाएँ',
    },
    createForm: {
      title: 'एजेंट बनाएं',
      brand: 'ब्रांड',
      name: 'अंदरूनी नाम',
      nameHint: 'यह नाम सिर्फ़ स्टाफ़ देखता है।',
      nameRequired: 'अंदरूनी नाम डालें।',
      brandRequired: 'ब्रांड चुनें।',
      makeDefault: 'इसे ब्रांड का डिफ़ॉल्ट एजेंट बनाएं',
      makeDefaultHint: 'डिफ़ॉल्ट एजेंट ब्रांड की बातचीत में जवाब देता है।',
      submit: 'एजेंट बनाएं',
      created: '{{name}} ड्राफ़्ट के रूप में बन गया।',
      noBrands: 'पहले एक ब्रांड बनाएं।',
    },
  },
  editor: {
    loading: 'एजेंट लोड हो रहा है',
    allAgents: 'सभी एजेंट',
    brand: 'ब्रांड: {{name}}',
    notLive: {
      title: 'सेव किया हुआ लाइव नहीं है',
      live: 'जब तक आप प्रकाशित नहीं करते, ग्राहकों को वर्शन {{version}} मिलता रहेगा।',
      none: 'जब तक आप कोई वर्शन प्रकाशित नहीं करते, यह एजेंट ग्राहकों को जवाब नहीं देता।',
      draftAhead: 'ड्राफ़्ट वर्शन {{version}} में ऐसे बदलाव हैं जो अभी लाइव नहीं हैं।',
    },
    unsaved: 'बिना सेव किए बदलाव',
    allSaved: 'सभी बदलाव सेव हो गए',
    readOnly: 'आप यह एजेंट देख सकते हैं, पर बदल नहीं सकते।',
    sections: {
      identity: 'पहचान',
      behaviour: 'व्यवहार',
      model: 'मॉडल',
      limits: 'सीमाएं',
      knowledge: 'जानकारी',
      memory: 'ग्राहक मेमोरी',
    },
    displayName: 'ग्राहकों को दिखने वाला नाम',
    displayNameHint:
      'हर AI संदेश के साथ दिखता है। भूमिका बताएं, जैसे “स्टोर असिस्टेंट”। किसी व्यक्ति का नाम इस्तेमाल न करें।',
    purpose: 'उद्देश्य',
    purposeHint: 'यह एजेंट किस लिए है, एक-दो वाक्यों में।',
    instructions: 'निर्देश',
    instructionsHint:
      'आपके कारोबार के नियम और अंदाज़। Hamnivo ईमानदारी और सुरक्षा के नियम जोड़ता है, जिन्हें हटाया नहीं जा सकता।',
    tone: 'लहजा',
    tones: {
      warm: 'आत्मीय',
      warmHint: 'दोस्ताना और बातचीत वाला।',
      neutral: 'सामान्य',
      neutralHint: 'सीधा और सरल।',
      formal: 'औपचारिक',
      formalHint: 'विनम्र और सटीक।',
    },
    languages: 'जवाब की भाषाएं',
    languagesHint: 'किसी दूसरी भाषा में लिखने वाले ग्राहक को किसी व्यक्ति से बात करने का विकल्प दिया जाता है।',
    otherLanguages: 'दूसरी भाषाओं के टैग',
    otherLanguagesHint: 'वैकल्पिक। कॉमा से अलग करें, जैसे hi-Latn, ur-Latn।',
    mode: 'मोड',
    modes: {
      answer: 'ग्राहकों को जवाब दें',
      answerHint: 'चैट में जवाब देता है।',
      assist: 'स्टाफ़ की मदद करें',
      assistHint:
        'आपकी टीम के लिए जवाब का ड्राफ़्ट लिखता है, जिसे वे मंज़ूर करें, बदलें या अस्वीकार करें। ग्राहकों को ख़ुद कभी जवाब नहीं देता।',
    },
    provider: 'प्रोवाइडर',
    providers: {
      anthropic: 'Anthropic',
      openai: 'OpenAI',
      google: 'Google Gemini',
      'openai-compatible': 'OpenAI-संगत सर्वर',
      mock: 'Hamnivo टेस्ट मॉडल (ऑफ़लाइन)',
    },
    modelId: 'मॉडल ID',
    modelIdHint: 'ठीक वैसे ही जैसे प्रोवाइडर उसे नाम देता है।',
    credentials: 'क्रेडेंशियल',
    platformKey: 'सर्वर कुंजी (Hamnivo शुरू होने पर सेट होती है)',
    credentialsHint: 'सेव किया हुआ मॉडल कनेक्शन इस्तेमाल करें, या वह कुंजी जिससे सर्वर शुरू किया गया था।',
    connectionsHidden: 'आप मॉडल कनेक्शन नहीं देख सकते, इसलिए मौजूदा चुनाव बना रहता है।',
    unknownConnection: 'सेव किया हुआ कनेक्शन {{id}}',
    fallback: 'बैकअप मॉडल',
    fallbackHint:
      'जब मुख्य मॉडल पर ज़्यादा लोड हो या वह उपलब्ध न हो, तो क्रम से आज़माए जाते हैं। ज़्यादा से ज़्यादा 2।',
    fallbackItem: 'बैकअप {{n}}',
    addFallback: 'बैकअप मॉडल जोड़ें',
    removeFallback: 'बैकअप {{n}} हटाएं',
    limits: {
      hint: 'जब कोई रन किसी सीमा तक पहुंचता है, तो वह रुक जाता है और बातचीत किसी व्यक्ति को सौंप देता है।',
      maxOutputTokens: 'सबसे लंबा जवाब (टोकन)',
      timeout: 'समय-सीमा (सेकंड)',
      historyMessages: 'पढ़े जाने वाले पिछले संदेश',
      maxSteps: 'एक रन में ज़्यादा से ज़्यादा चरण',
    },
    knowledge: {
      hint: 'ग्राहकों के जवाब सिर्फ़ सार्वजनिक स्रोतों का इस्तेमाल करते हैं।',
      none: 'इस ब्रांड के पास अभी जानकारी का कोई स्रोत नहीं है।',
      manage: 'जानकारी प्रबंधित करें',
      hidden: 'आप जानकारी के स्रोत नहीं देख सकते।',
    },
    memory: {
      enabled: 'ग्राहकों की पसंद याद रखें',
      hint: 'सिर्फ़ उन ग्राहकों के लिए जो चैट में सहमति देते हैं। वे देख सकते हैं कि क्या याद रखा गया है, उसे ठीक कर सकते हैं, हटा सकते हैं और मेमोरी बंद कर सकते हैं।',
      maxItems: 'एक जवाब में ज़्यादा से ज़्यादा यादें',
      maxChars: 'एक जवाब में मेमोरी के ज़्यादा से ज़्यादा अक्षर',
    },
    save: 'ड्राफ़्ट सेव करें',
    saved: 'ड्राफ़्ट वर्शन {{version}} के रूप में सेव हो गया।',
    discard: 'बदलाव छोड़ें',
    publish: 'वर्शन {{version}} प्रकाशित करें',
    publishNone: 'प्रकाशित करें',
    publishWhy: {
      unsaved: 'प्रकाशित करने से पहले ड्राफ़्ट सेव करें।',
      noDraft: 'प्रकाशित करने के लिए कोई नया ड्राफ़्ट नहीं है।',
      noPermission: 'एजेंट प्रकाशित करने के लिए आपको अनुमति चाहिए।',
      archived: 'आर्काइव किए गए एजेंट प्रकाशित नहीं किए जा सकते।',
    },
    publishConfirm: {
      title: 'वर्शन {{version}} प्रकाशित करें?',
      body: 'ग्राहकों को तुरंत वर्शन {{version}} से जवाब मिलने लगेंगे।',
      replaces: 'वर्शन {{version}} जवाब देना बंद कर देगा।',
      first: 'यह पहला वर्शन है जो ग्राहक देखेंगे।',
      submit: 'प्रकाशित करें',
    },
    published: 'वर्शन {{version}} लाइव है।',
    publishFailed: 'वर्शन {{version}} प्रकाशित नहीं हुआ। लाइव वर्शन नहीं बदला।',
    conflict: {
      title: 'किसी ने नया ड्राफ़्ट सेव किया है',
      body: 'आपके बदलाव करते समय वर्शन {{version}} सेव हुआ। आपके बदलाव अभी भी यहीं हैं।',
      differences: 'नए ड्राफ़्ट से अलग फ़ील्ड:',
      same: 'नए ड्राफ़्ट में वही मान हैं जो आपके पास हैं।',
      loadTheirs: 'नया ड्राफ़्ट लोड करें',
      keepMine: 'मेरे बदलाव रखें',
      keepMineHint: 'आपका वर्शन नए ड्राफ़्ट के ऊपर सेव करता है।',
    },
    versions: {
      title: 'वर्शन',
      loading: 'वर्शन लोड हो रहे हैं',
      state: { draft: 'ड्राफ़्ट', published: 'लाइव', retired: 'पुराना' },
      version: 'वर्शन {{version}}',
      hash: 'हैश',
      noHash: 'प्रकाशित होने पर तय होता है',
      created: 'बनाया गया: {{time}}',
      publishedAt: 'प्रकाशित: {{time}}',
      rollback: 'वर्शन {{version}} पर वापस जाएं',
      rollbackTitle: 'वर्शन {{version}} पर वापस जाएं?',
      rollbackBody:
        'वर्शन {{version}} फिर से लाइव हो जाएगा। आपका ड्राफ़्ट बना रहेगा और बाद में प्रकाशित किया जा सकता है।',
      rollbackSubmit: 'वापस जाएं',
      rolledBack: 'वर्शन {{version}} फिर से लाइव है।',
      empty: 'अभी कोई वर्शन नहीं।',
    },
    leave: {
      title: 'बिना सेव किए छोड़ें?',
      description: 'इस एजेंट में आपके बदलाव सेव नहीं हुए हैं।',
      save: 'ड्राफ़्ट सेव करें',
      discard: 'छोड़ें',
      stay: 'यहीं रहें',
    },
    errors: {
      summary: 'सेव करने के लिए ये फ़ील्ड ठीक करें',
      displayName: 'ग्राहकों को दिखने वाला नाम डालें, 60 अक्षरों तक।',
      purpose: 'उद्देश्य 2,000 अक्षरों से कम रखें।',
      instructions: 'निर्देश 20,000 अक्षरों से कम रखें।',
      languages: 'कम से कम एक भाषा चुनें, और 20 से ज़्यादा नहीं।',
      languageTag: '{{tag}} भाषा टैग नहीं है। hi-Latn जैसा रूप इस्तेमाल करें।',
      modelId: 'मॉडल ID डालें।',
      number: '{{min}} से {{max}} तक कोई पूर्ण संख्या डालें।',
      knowledge: '20 से ज़्यादा स्रोत न चुनें।',
    },
  },
  models: {
    title: 'मॉडल कनेक्शन',
    description:
      'मॉडल प्रोवाइडर की API कुंजियां। कुंजियां एन्क्रिप्ट करके रखी जाती हैं और सेव करने के बाद फिर कभी नहीं दिखतीं।',
    loading: 'मॉडल कनेक्शन लोड हो रहे हैं',
    add: 'कनेक्शन जोड़ें',
    addTitle: 'मॉडल कनेक्शन जोड़ें',
    provider: 'प्रोवाइडर',
    label: 'लेबल',
    labelHint: 'आपके और आपकी टीम के लिए, जैसे “OpenAI प्रोडक्शन”।',
    baseUrl: 'बेस URL',
    baseUrlHint: 'OpenAI-संगत सर्वर के लिए ज़रूरी। दूसरों के लिए वैकल्पिक (प्रॉक्सी या क्षेत्र)।',
    apiKey: 'API कुंजी',
    apiKeyHint: 'एन्क्रिप्ट करके रखी जाती है। आप इसे फिर नहीं देखेंगे, सिर्फ़ इसके आखिरी अक्षर।',
    allowedModels: 'अनुमति वाले मॉडल',
    allowedModelsHint: 'वैकल्पिक। हर लाइन में एक मॉडल ID। किसी भी मॉडल की अनुमति के लिए खाली छोड़ें।',
    submit: 'कनेक्शन सेव करें',
    added: 'कनेक्शन सेव हो गया। कुंजी एन्क्रिप्ट करके रखी गई है और फिर नहीं दिखेगी। इसके आखिरी अक्षर {{hint}} हैं।',
    columns: {
      label: 'लेबल',
      provider: 'प्रोवाइडर',
      key: 'कुंजी',
      models: 'अनुमति वाले मॉडल',
      status: 'स्थिति',
      created: 'जोड़ा गया',
      actions: 'कार्रवाइयां',
    },
    anyModel: 'कोई भी मॉडल',
    keyEnding: 'आखिरी अक्षर {{hint}}',
    status: { active: 'सक्रिय', disabled: 'बंद' },
    disable: 'बंद करें',
    disableLabel: '{{label}} बंद करें',
    disableTitle: '{{label}} बंद करें?',
    disableBody:
      'इसका इस्तेमाल करने वाले एजेंट तब तक जवाब नहीं देंगे जब तक आप दूसरे क्रेडेंशियल नहीं चुनते। रखी हुई कुंजी रद्द हो जाती है और वापस नहीं मिल सकती।',
    disableSubmit: 'कनेक्शन बंद करें',
    disabled: '{{label}} बंद है।',
    empty: {
      title: 'अभी कोई मॉडल कनेक्शन नहीं',
      description:
        'इसके बिना एजेंट सिर्फ़ उन्हीं प्रोवाइडर का इस्तेमाल कर सकते हैं जिनकी कुंजियां सर्वर के पास हैं, और ऑफ़लाइन टेस्ट मॉडल का।',
    },
    errors: {
      label: 'लेबल डालें।',
      apiKey: 'API कुंजी डालें। इसमें कम से कम 8 अक्षर होते हैं।',
      baseUrl: 'http:// या https:// से शुरू होने वाला पता डालें।',
      baseUrlRequired: 'OpenAI-संगत सर्वर के लिए बेस URL ज़रूरी है।',
    },
  },
  knowledge: {
    title: 'जानकारी',
    description: 'वे स्रोत जिनसे AI जवाब देता है। जवाबों में इस्तेमाल से पहले दस्तावेज़ों की जांच और इंडेक्सिंग होती है।',
    sources: {
      title: 'स्रोत',
      loading: 'स्रोत लोड हो रहे हैं',
      add: 'स्रोत जोड़ें',
      documents: 'दस्तावेज़: {{total}}',
      review: 'हर {{days}} दिन में समीक्षा',
      noReview: 'कोई समीक्षा चक्र नहीं',
      settings: '{{name}} की सेटिंग',
      empty: {
        title: 'अपना पहला भरोसेमंद स्रोत जोड़ें',
        description: 'आपके पास पहले से मौजूद सामग्री से AI को सही जानकारी दें।',
      },
    },
    visibility: {
      public: 'सार्वजनिक जवाब',
      internal: 'सिर्फ़ टीम',
      publicHint: 'ग्राहकों के जवाबों का आधार बन सकता है।',
      internalHint:
        'सिर्फ़ आपकी टीम के लिए, जैसे जानकारी खोजने में। ग्राहकों के जवाबों या ड्राफ़्ट में कभी इस्तेमाल नहीं होता।',
    },
    sourceStatus: { active: 'सक्रिय', paused: 'रुका हुआ', retired: 'इस्तेमाल बंद' },
    sourceForm: {
      addTitle: 'जानकारी का स्रोत जोड़ें',
      editTitle: 'स्रोत की सेटिंग',
      brand: 'ब्रांड',
      name: 'स्रोत का नाम',
      nameRequired: 'स्रोत का नाम डालें।',
      visibility: 'इससे मिले जवाब कौन देख सकता है',
      review: 'समीक्षा चक्र (दिनों में)',
      reviewHint:
        'वैकल्पिक। जिन दस्तावेज़ों की इतने दिनों में समीक्षा नहीं होती, वे तब तक जवाबों का आधार नहीं बनते जब तक कोई उनकी समीक्षा न करे।',
      reviewInvalid: '1 से 3650 तक दिनों की पूर्ण संख्या डालें, या खाली छोड़ें।',
      status: 'स्थिति',
      submit: 'स्रोत जोड़ें',
      save: 'सेटिंग सेव करें',
      created: '{{name}} जोड़ा गया।',
      saved: 'सेटिंग सेव हो गई।',
    },
    documents: {
      title: 'दस्तावेज़',
      loading: 'दस्तावेज़ लोड हो रहे हैं',
      add: 'दस्तावेज़ जोड़ें',
      pick: 'दस्तावेज़ देखने के लिए कोई स्रोत चुनें।',
      polling: 'जब दस्तावेज़ प्रोसेस हो रहे होते हैं, Studio हर कुछ सेकंड में अपडेट जांचता है।',
      columns: {
        title: 'दस्तावेज़',
        state: 'स्थिति',
        freshness: 'ताज़गी',
        version: 'वर्शन',
      },
      select: '{{title}} का विवरण दिखाएं',
      empty: {
        title: 'इस स्रोत में अभी कोई दस्तावेज़ नहीं',
        description:
          'टेक्स्ट पेस्ट करें या फ़ाइल अपलोड करें। जवाबों में इस्तेमाल से पहले इसकी जांच और इंडेक्सिंग होती है।',
      },
    },
    state: {
      queued: 'कतार में',
      processing: 'प्रोसेस हो रहा है',
      ready: 'तैयार',
      failed: 'विफल',
      rejected: 'अस्वीकार',
      superseded: 'बदला गया',
      retired: 'इस्तेमाल बंद',
    },
    freshness: { current: 'ताज़ा', stale: 'समीक्षा ज़रूरी', expired: 'समय-सीमा खत्म' },
    detail: {
      title: 'दस्तावेज़ का विवरण',
      back: 'दस्तावेज़ों पर वापस जाएं',
      answering: 'जवाब देने वाला वर्शन',
      newest: 'सबसे नया वर्शन',
      noneAnswering: 'अभी कोई वर्शन जवाब नहीं देता।',
      version: 'वर्शन {{version}}',
      size: 'आकार',
      passages: 'अंश',
      added: 'जोड़ा गया',
      readySince: 'कब से तैयार',
      validUntil: 'कब तक इस्तेमाल होगा',
      reviewed: 'पिछली समीक्षा',
      neverReviewed: 'अभी समीक्षा नहीं हुई',
      reason: 'कारण: {{reason}}',
      original: 'मूल दस्तावेज़ खोलें',
      keepsAnswering: 'नया वर्शन तैयार होने तक वर्शन {{version}} जवाब देता रहेगा।',
      markReviewed: 'समीक्षा हो गई, यह दर्ज करें',
      markedReviewed: '{{title}} की समीक्षा दर्ज की गई।',
      retire: 'इस्तेमाल बंद करें',
      retireTitle: '{{title}} का इस्तेमाल बंद करें?',
      retireBody:
        'जवाबों में यह दस्तावेज़ इस्तेमाल होना बंद हो जाएगा। यह सूची में “इस्तेमाल बंद” के रूप में बना रहेगा।',
      retireSubmit: 'दस्तावेज़ का इस्तेमाल बंद करें',
      retired: '{{title}} का इस्तेमाल बंद कर दिया गया।',
      newVersion: 'नया वर्शन अपलोड करें',
    },
    upload: {
      addTitle: 'दस्तावेज़ जोड़ें',
      versionTitle: '{{title}} का नया वर्शन',
      docTitle: 'शीर्षक',
      titleRequired: 'शीर्षक डालें।',
      url: 'मूल पता (URL)',
      urlHint: 'वैकल्पिक। उद्धरणों के साथ दिखता है ताकि लोग मूल पढ़ सकें।',
      urlInvalid: 'http:// या https:// से शुरू होने वाला पता डालें।',
      language: 'भाषा टैग',
      languageHint: 'वैकल्पिक, जैसे en, ar या hi-Latn।',
      languageInvalid: 'en या hi-Latn जैसा भाषा टैग इस्तेमाल करें।',
      content: 'सामग्री',
      paste: 'टेक्स्ट पेस्ट करें',
      file: 'फ़ाइल अपलोड करें',
      format: 'फ़ॉर्मैट',
      formats: { markdown: 'Markdown', plain: 'सादा टेक्स्ट', html: 'HTML' },
      text: 'टेक्स्ट',
      textRequired: 'टेक्स्ट पेस्ट करें।',
      fileLabel: 'फ़ाइल',
      fileHint: 'PDF, Word (.docx), HTML, Markdown या सादा टेक्स्ट, 5 MB तक।',
      fileRequired: 'फ़ाइल चुनें।',
      fileTooLarge: 'यह फ़ाइल {{size}} की है। फ़ाइल 5 MB तक की हो सकती है।',
      fileType:
        'इस तरह की फ़ाइल समर्थित नहीं है। PDF, Word (.docx), HTML, Markdown या सादा टेक्स्ट इस्तेमाल करें।',
      fileChosen: 'चुनी गई: {{name}}, {{size}}',
      validUntil: 'इसके बाद इस्तेमाल बंद करें',
      validUntilHint: 'वैकल्पिक, जैसे किसी मौसमी नीति के लिए।',
      submit: 'दस्तावेज़ जोड़ें',
      submitVersion: 'वर्शन अपलोड करें',
      queued: '{{title}} जांच के लिए कतार में है।',
    },
    search: {
      title: 'खोज का पूर्वावलोकन',
      description: 'देखें कि कोई जवाब किन अंशों का इस्तेमाल करेगा।',
      brand: 'ब्रांड',
      audience: 'किसके लिए जवाब',
      audiences: {
        customer: 'ग्राहक (सिर्फ़ सार्वजनिक स्रोत)',
        staff: 'स्टाफ़ (सभी स्रोत)',
      },
      query: 'सवाल',
      queryRequired: 'सवाल डालें।',
      submit: 'खोजें',
      results: 'अंश, सबसे अच्छे पहले',
      found: 'मिले अंश: {{total}}',
      score: 'स्कोर {{score}}',
      version: 'वर्शन {{version}}',
      empty:
        'कोई अंश मेल नहीं खाता। जवाब में ज़्यादा जानकारी मांगी जाएगी या किसी व्यक्ति से बात करने का विकल्प दिया जाएगा।',
      noBrands: 'जानकारी खोजने के लिए एक ब्रांड बनाएं।',
    },
  },
  stopAi: {
    title: 'AI रोकें',
    description: 'AI के जवाब तुरंत रोकें। लोग बातचीत जारी रखते हैं, और नए संदेश आपकी टीम के पास जाते हैं।',
    loading: 'रोकने के स्विच लोड हो रहे हैं',
    dialogTitle: 'AI के जवाब रोकें',
    dialogDescription: 'आप जहां चुनें, वहां AI जवाब देना बंद कर देता है। बातचीत आपकी टीम के लिए खुली रहती है।',
    scope: 'कहां रोकें',
    scopes: {
      tenant: 'इस वर्कस्पेस में हर जगह',
      brand: 'एक ब्रांड',
      agent: 'एक एजेंट',
    },
    brand: 'ब्रांड',
    agent: 'एजेंट',
    choose: 'चुनें…',
    reason: 'कारण',
    reasonHint: 'आपकी टीम यह देखती है। उदाहरण: “जवाबों में गलत कीमतें”।',
    submit: 'AI के जवाब रोकें',
    stopped: 'AI के जवाब रोक दिए गए। {{scope}}।',
    errors: {
      reason: 'कम से कम 3 अक्षरों का कारण बताएं।',
      target: 'चुनें कि AI कहां रोकना है।',
    },
    active: { title: 'चालू रोक', empty: 'AI कहीं भी रुका नहीं है।' },
    past: { title: 'पिछली रोक', empty: 'कोई पिछली रोक नहीं।' },
    columns: {
      since: 'रोका गया',
      by: 'किसने',
      cleared: 'फिर शुरू हुआ',
    },
    status: { active: 'AI रुका हुआ है', cleared: 'फिर शुरू हुआ' },
    clear: 'AI को फिर से जवाब देने दें',
    clearLabel: 'AI को फिर से जवाब देने दें: {{scope}}',
    clearTitle: 'AI को फिर से जवाब देने दें?',
    clearBody: 'इसके लिए AI के जवाब फिर शुरू होंगे: {{scope}}। पक्का करें कि रोकने की वजह ठीक हो गई है।',
    clearSubmit: 'AI को जवाब देने दें',
    cleared: 'AI के जवाब फिर शुरू हो गए। {{scope}}।',
    noPermission: 'AI रोकने या फिर शुरू करने के लिए आपको एजेंट प्रकाशित करने की अनुमति चाहिए।',
    you: 'आप',
    teammate: 'एक साथी',
    unknownTarget: 'अज्ञात ({{id}})',
  },
  runs: {
    title: 'रन',
    description:
      'हर AI रन ने क्या किया: नतीजा, वर्शन, मॉडल, टोकन और समय। प्रॉम्प्ट और जवाब यहां नहीं रखे जाते।',
    loading: 'रन लोड हो रहे हैं',
    filters: {
      label: 'रन फ़िल्टर करें',
      agent: 'एजेंट',
      allAgents: 'सभी एजेंट',
      outcome: 'नतीजा',
      allOutcomes: 'सभी नतीजे',
      conversation: 'बातचीत ID',
      conversationHint: 'वैकल्पिक, conv_ से शुरू होता है',
      apply: 'फ़िल्टर लागू करें',
      reset: 'फ़िल्टर रीसेट करें',
      outcomeNote: 'नतीजे का फ़िल्टर इस पेज पर लोड हुए रन पर लागू होता है।',
    },
    columns: {
      started: 'शुरू हुआ',
      outcome: 'नतीजा',
      agent: 'एजेंट और वर्शन',
      model: 'मॉडल',
      tokens: 'टोकन अंदर / बाहर',
      latency: 'लगा समय',
      details: 'विवरण',
    },
    outcome: {
      replied: 'जवाब दिया',
      drafted: 'स्टाफ़ के लिए ड्राफ़्ट',
      handoff: 'व्यक्ति को सौंपा',
      fenced: 'रद्द, बातचीत आगे बढ़ गई',
      blocked: 'गार्ड ने रोका',
      error: 'त्रुटि',
      skipped: 'छोड़ा गया',
      cancelled: 'किसी व्यक्ति ने रोका',
      running: 'चल रहा है',
    },
    view: 'देखें',
    viewLabel: '{{time}} को शुरू हुआ रन देखें',
    loadMore: 'और रन लोड करें',
    shown: 'दिखाए गए रन: {{total}}',
    empty: {
      title: 'अभी कोई रन नहीं',
      description: 'जब AI किसी बातचीत में जवाब देता है या ड्राफ़्ट लिखता है, तब रन दिखते हैं।',
      action: 'AI एजेंट खोलें',
    },
    noMatches: {
      title: 'इन फ़िल्टर से कोई रन मेल नहीं खाता',
      description: 'और रन देखने के लिए कोई फ़िल्टर हटाएं।',
    },
    detail: {
      title: 'रन का विवरण',
      loading: 'रन लोड हो रहा है',
      summary: 'सारांश',
      states: {
        running: 'चल रहा है',
        succeeded: 'सफल',
        failed: 'विफल',
        skipped: 'छोड़ा गया',
      },
      kind: 'प्रकार',
      kinds: { reply: 'ग्राहक को जवाब', draft: 'स्टाफ़ के लिए ड्राफ़्ट', guard: 'संदेश गार्ड' },
      conversation: 'बातचीत',
      started: 'शुरू हुआ',
      finished: 'खत्म हुआ',
      errorKind: 'त्रुटि का प्रकार',
      steps: 'चरण',
      noSteps: 'कोई चरण दर्ज नहीं हुआ।',
      step: 'चरण {{n}}',
      stepKinds: {
        model: 'मॉडल',
        guard: 'गार्ड',
        retrieval: 'जानकारी की खोज',
        decision: 'फ़ैसला',
        tool: 'टूल',
      },
      stepStatus: { ok: 'ठीक', error: 'त्रुटि', blocked: 'रोका गया', skipped: 'छोड़ा गया' },
      noDetail: 'और कोई विवरण नहीं।',
      version: 'v{{version}}',
      noAgent: 'कोई एजेंट नहीं',
    },
  },
  inbox: {
    title: 'इनबॉक्स',
    documentTitle: '{{name}} · इनबॉक्स',
    loading: 'बातचीत लोड हो रही हैं',
    conversationLoading: 'बातचीत लोड हो रही है',
    loadMore: 'और बातचीत दिखाएँ',
    shown: 'दिखाई गई बातचीत: {{n}}',
    unavailable: 'यह बातचीत उपलब्ध नहीं है',
    notFound: 'यह किसी ऐसे वर्कस्पेस या ब्रांड में हो सकती है जिसे आप नहीं देख सकते, या लिंक गलत है।',
    back: 'इनबॉक्स पर वापस जाएँ',
    forbidden: {
      title: 'आप यहाँ बातचीत नहीं देख सकते',
      description: 'वर्कस्पेस के मालिक या एडमिन से ऐसी भूमिका माँगें जिसमें इनबॉक्स शामिल हो।',
    },
    queues: {
      label: 'कतारें',
      mine: 'मेरी',
      unassigned: 'किसी व्यक्ति का इंतज़ार',
      overdue: 'देरी वाली',
      ai: 'AI जवाब दे रहा है',
      team: 'मेरी टीमें',
      tickets: 'टिकट',
      all: 'सभी',
    },
    tickets: ticketsHi,
    sla: slaHi,
    aiDrafts: aiDraftsHi,
    offers: offersHi,
    blocking: blockingHi,
    keys: {
      plain: 'कुंजियाँ: चलने के लिए ↑ ↓ या J K, खोलने के लिए Enter, वापस आने के लिए Esc।',
      withSelect: 'कुंजियाँ: चलने के लिए ↑ ↓ या J K, खोलने के लिए Enter, चुनने के लिए X, वापस आने के लिए Esc।',
    },
    search: {
      label: 'बातचीत और संदेशों में खोजें',
      placeholder: 'नाम, ईमेल या संदेश का टेक्स्ट खोजें',
      submit: 'खोजें',
      loading: 'खोज रहे हैं',
      tooShort: 'कम से कम 2 अक्षर लिखें।',
      found: 'बातचीत: {{conversations}}। संदेश: {{messages}}।',
      conversations: 'ग्राहक',
      messages: 'संदेश',
      none: 'कुछ नहीं मिला',
      noneHint: 'सिर्फ़ वही बातचीत खोजी जाती हैं जिन्हें आप देख सकते हैं।',
      clear: 'खोज साफ़ करें',
      more: 'और संदेश दिखाएँ',
    },
    filters: {
      button: 'फ़िल्टर',
      buttonCount: 'फ़िल्टर ({{n}})',
      title: 'कतार फ़िल्टर करें',
      close: 'फ़िल्टर बंद करें',
      status: 'स्थिति',
      statuses: {
        active: 'खुली या लंबित',
        snoozed: 'स्नूज़ की गई',
        resolved: 'हल हो चुकी',
        all: 'कोई भी स्थिति',
      },
      brand: 'ब्रांड',
      allBrands: 'सभी ब्रांड',
      channel: 'चैनल',
      allChannels: 'सभी चैनल',
      language: 'ग्राहक की भाषा',
      allLanguages: 'सभी भाषाएँ',
      tag: 'टैग',
      allTags: 'सभी टैग',
      active: 'चालू फ़िल्टर: {{n}}',
      clear: 'फ़िल्टर हटाएँ',
    },
    empty: {
      filteredTitle: 'इन फ़िल्टर से कोई बातचीत मेल नहीं खाती',
      filteredDescription: 'पूरी कतार देखने के लिए फ़िल्टर हटाएँ।',
      seeAll: 'सभी बातचीत देखें',
      overview: 'ओवरव्यू पर जाएँ',
      mine: {
        title: 'आपको कुछ नहीं सौंपा गया है',
        description: 'जो बातचीत आप संभालते हैं या आपको दी जाती हैं, वे यहाँ दिखती हैं।',
      },
      unassigned: {
        title: 'कोई किसी व्यक्ति का इंतज़ार नहीं कर रहा',
        description: 'जब कोई ग्राहक किसी व्यक्ति से बात करना चाहता है, तो बातचीत यहाँ इंतज़ार करती है।',
      },
      overdue: {
        title: 'कुछ भी देर से नहीं है',
        description: 'जवाब के समय का लक्ष्य पार कर चुकी बातचीत यहाँ दिखती हैं, सबसे ज़्यादा देरी वाली पहले।',
      },
      ai: {
        title: 'AI अभी किसी को जवाब नहीं दे रहा',
        description: 'जिन बातचीत का जवाब AI दे रहा है, वे यहाँ दिखती हैं।',
      },
      team: {
        title: 'आपकी टीमों में कुछ नहीं है',
        description: 'आपकी टीमों को दी गई बातचीत यहाँ दिखती हैं।',
      },
      tickets: {
        title: 'अभी कोई टिकट नहीं',
        description:
          'जब कोई जवाब देने के लिए न हो, तब संपर्क फ़ॉर्म से छोड़े गए संदेश टिकट बन जाते हैं। वह हर बातचीत भी, जिसे आप टिकट बनाते हैं।',
      },
      all: {
        title: 'अभी कोई बातचीत नहीं',
        description: 'जब ग्राहक किसी जुड़े हुए ब्रांड से लिखते हैं, तो बातचीत यहाँ दिखती हैं।',
      },
    },
    pick: {
      title: 'एक बातचीत चुनें',
      description: 'पढ़ने और जवाब देने के लिए कतार से एक बातचीत खोलें।',
    },
    connection: {
      reconnecting:
        'फिर से जुड़ रहे हैं… अपडेट देर से आ सकते हैं; इनबॉक्स हर 15 सेकंड में जाँच करता है। आपके ड्राफ़्ट सहेजे हुए हैं।',
      offline: 'आप ऑफ़लाइन हैं। वापस आने तक आपके ड्राफ़्ट इसी ब्राउज़र में रहेंगे।',
    },
    row: {
      noMessages: 'अभी कोई संदेश नहीं',
      team: 'टीम: {{name}}',
      from: { ai: 'AI:', staff: 'स्टाफ़:', system: 'नोट:' },
    },
    owner: {
      ai: 'AI जवाब दे रहा है',
      waiting: 'किसी व्यक्ति का इंतज़ार',
      waitingFor: 'इंतज़ार: {{time}}',
      paused: 'AI रुका हुआ है',
      you: 'आप',
      person: 'एक व्यक्ति',
      external: 'Chatwoot में कोई व्यक्ति',
    },
    status: {
      open: 'खुली',
      pending: 'लंबित',
      snoozed: 'स्नूज़ की गई',
      snoozedUntil: '{{time}} तक स्नूज़',
      resolved: 'हल हो चुकी',
      resolve: 'हल करें',
      resolvedDone: 'बातचीत हल कर दी गई।',
      reopen: 'फिर से खोलें',
      wake: 'स्नूज़ हटाएँ',
      reopened: 'बातचीत फिर से खुल गई। अब आप इसे संभाल रहे हैं।',
    },
    identity: {
      verified: 'सत्यापित ग्राहक',
      anonymous: 'साइन इन नहीं है',
      unknown: 'अज्ञात ग्राहक',
    },
    channel: {
      chat: 'वेबसाइट चैट',
      email: 'ईमेल',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      sms: 'SMS',
      other: 'दूसरा चैनल',
    },
    details: {
      title: 'विवरण',
      show: 'विवरण',
      hide: 'विवरण छिपाएँ',
    },
    banner: {
      ai: '{{name}} जवाब दे रहा है। जवाब देने या बातचीत संभालने से इस बातचीत में AI रुक जाता है।',
      other: '{{name}} यह बातचीत संभाल रहे हैं।',
      otherSince: '{{name}} यह बातचीत संभाल रहे हैं। उन्होंने इसे {{time}} लिया।',
      notesStill: 'आप अब भी आंतरिक नोट जोड़ सकते हैं।',
      asked: 'ग्राहक ने किसी व्यक्ति से बात करनी चाही।',
      askedSince: 'ग्राहक ने {{time}} किसी व्यक्ति से बात करनी चाही।',
      released: 'यह बातचीत कतार में वापस रखी गई।',
      channelAiOff: 'इस चैनल पर AI जवाब नहीं देता, इसलिए किसी व्यक्ति को जवाब देना होगा।',
      waiting: 'यह बातचीत किसी व्यक्ति का इंतज़ार कर रही है।',
      takeOver: 'बातचीत संभालें',
      claim: 'मुझे सौंपें',
      claimLost: 'किसी और ने यह बातचीत पहले ले ली। अब दिख रहा है कि इसे कौन संभाल रहा है।',
    },
    presence: {
      viewing: '{{name}} देख रहे हैं',
      typingReply: '{{name}} जवाब लिख रहे हैं',
      typingNote: '{{name}} नोट लिख रहे हैं',
      alsoReply: '{{name}} भी यहाँ जवाब लिख रहे हैं।',
      alsoNote: '{{name}} भी यहाँ नोट लिख रहे हैं।',
    },
    assign: {
      button: 'सौंपें',
      me: 'मुझे सौंपें',
      colleagues: 'जवाब दे सकने वाले साथी',
      current: 'अभी इनके पास है',
      queue: 'कतार में वापस',
      ai: 'AI को वापस दें',
      team: 'टीम',
      teamCurrent: 'टीम: {{name}}',
      noTeams: 'अभी कोई टीम नहीं',
      noTeam: 'कोई टीम नहीं',
      doneMe: 'अब आप यह बातचीत संभाल रहे हैं।',
      doneTo: '{{name}} को सौंपी गई।',
      doneQueue: 'कतार में वापस रखी गई।',
      doneAi: 'AI फिर से जवाब दे रहा है।',
      doneTeam: '{{name}} टीम को सौंपी गई।',
      doneNoTeam: 'टीम से हटाई गई।',
    },
    transfer: {
      ask: 'बातचीत संभालने का अनुरोध करें',
      title: 'बातचीत संभालने का अनुरोध करें',
      description:
        '{{name}} को एक आंतरिक नोट मिलेगा जिसमें यह बातचीत आपको देने को कहा जाएगा। जब तक वे या कोई सुपरवाइज़र ऐसा न करें, कुछ नहीं बदलता।',
      note: 'क्यों (आपके साथी के लिए)',
      send: 'अनुरोध भेजें',
      sent: '{{name}} को अनुरोध भेजा गया।',
    },
    snooze: {
      button: 'स्नूज़',
      hour: '1 घंटे के लिए',
      tomorrow: 'कल तक ({{time}})',
      nextWeek: 'अगले हफ़्ते तक ({{time}})',
      pick: 'समय चुनें…',
      pickTitle: 'कब तक स्नूज़ करें',
      pickDescription: 'इस समय पर, या ग्राहक के लिखने पर, बातचीत कतार में लौट आती है।',
      until: 'तारीख़ और समय',
      future: 'भविष्य का समय चुनें।',
      confirm: 'स्नूज़ करें',
      done: '{{time}} तक स्नूज़ की गई।',
    },
    timeline: {
      label: 'संदेश',
      loading: 'संदेश लोड हो रहे हैं',
      older: 'पुराने संदेश दिखाएँ',
      empty: 'अभी कोई संदेश नहीं।',
    },
    roles: {
      customer: 'ग्राहक',
      staff: 'सपोर्ट एजेंट',
      you: 'आप',
      ai: 'AI सहायक',
      specialist: 'AI विशेषज्ञ',
      guest: 'मेहमान',
      system: 'सिस्टम',
    },
    lanes: {
      public: 'जवाब',
      publicDescription: 'ग्राहक जवाब देखता है।',
      internal: 'आंतरिक नोट',
      internalDescription: 'नोट सिर्फ़ आपकी टीम देखती है। ग्राहक इन्हें कभी नहीं देखता।',
    },
    message: {
      handoffSummary: 'हैंडऑफ़ सारांश',
      staffOnly: 'सिर्फ़ स्टाफ़ के लिए',
      sources: 'स्रोत',
      newFrom: '{{name}} का नया संदेश।',
      file: {
        quarantined: 'जाँच का इंतज़ार',
        scanning: 'जाँच हो रही है',
        available: 'उपलब्ध',
        rejected: 'रोकी गई',
      },
      place: 'एक जगह साझा की: {{label}}',
      contact: 'एक संपर्क साझा किया: {{details}}',
      reaction: '{{emoji}} से प्रतिक्रिया दी',
    },
    translation: {
      translate: '{{language}} में अनुवाद करें',
      label: '{{language}} में मशीन अनुवाद। मूल संदेश ऊपर है।',
      hide: 'छिपाएँ',
      show: 'दिखाएँ',
      failed: 'अनुवाद नहीं हो पाया। मूल संदेश नहीं बदला।',
    },
    composer: {
      lanes: 'जवाब या नोट लिखें',
      replyLabel: '{{name}} को जवाब',
      noteLabel: 'आंतरिक नोट',
      replyPlaceholder: '{{name}} को जवाब लिखें…',
      notePlaceholder: 'अपनी टीम के लिए नोट लिखें…',
      send: 'भेजें',
      addNote: 'नोट जोड़ें',
      toSend: 'भेजने के लिए',
      sent: 'जवाब भेजा गया।',
      noteAdded: 'नोट जोड़ा गया।',
      notSent: 'नहीं भेजा गया।',
      saving: 'ड्राफ़्ट सहेज रहे हैं…',
      saved: 'ड्राफ़्ट सहेजा गया',
      savedHere: 'ड्राफ़्ट इस ब्राउज़र में रखा है',
      offlineSaved: 'ऑफ़लाइन: ड्राफ़्ट इस ब्राउज़र में रखा है',
      hasDraft: '(ड्राफ़्ट)',
      takesOver: 'जवाब भेजने से यह बातचीत आपको सौंप दी जाती है।',
      takesOverAi: 'जवाब भेजने से बातचीत AI से आपके पास आ जाती है।',
      noReply: 'आपकी भूमिका यह बातचीत पढ़ सकती है, पर जवाब नहीं दे सकती।',
      noNote: 'आपकी भूमिका यहाँ नोट नहीं जोड़ सकती।',
      ownedByOther: '{{name}} यह बातचीत संभाल रहे हैं। जवाब देने से पहले इसे संभालने का अनुरोध करें।',
      staleSend:
        'नहीं भेजा गया: किसी और ने बदल दिया कि यह बातचीत कौन संभालेगा। आपका टेक्स्ट नीचे रखा है। बातचीत देखें, फिर दोबारा कोशिश करें।',
      conflict: 'यह ड्राफ़्ट किसी दूसरे टैब या डिवाइस में बदला गया।',
      keepMine: 'मेरा रखें',
      useTheirs: 'दूसरा संस्करण इस्तेमाल करें',
    },
    outgoing: {
      label: 'अभी न भेजे गए संदेश',
      sending: 'भेज रहे हैं',
      failed: 'नहीं भेजा गया',
      ownership: 'भेजने से पहले किसी और ने बातचीत संभाल ली। ग्राहक तक कुछ नहीं पहुँचा।',
      unavailable: 'Chatwoot ने जवाब नहीं दिया। दोबारा भेजना सुरक्षित है: संदेश कभी दो बार नहीं जाता।',
      other: 'यह भेजा नहीं जा सका। दोबारा कोशिश करें।',
      retry: 'दोबारा कोशिश करें',
      discard: 'हटाएँ',
      discarded: 'हटा दिया गया।',
    },
    saved: {
      button: 'सहेजे गए जवाब',
      search: 'सहेजे गए जवाब खोजें',
      none: 'कोई सहेजा गया जवाब नहीं मिला।',
      missing: 'खुद भरें: {{names}}',
    },
    copilot: {
      button: 'जवाब सुझाएँ',
      title: 'सुझाया गया जवाब',
      review: '{{name}} (AI) ने लिखा। भेजने से पहले जाँच लें; कुछ भी अपने-आप नहीं भेजा जाता।',
      noSources: 'किसी स्रोत का इस्तेमाल नहीं हुआ।',
      use: 'जवाब में बदलें',
      copy: 'कॉपी करें',
      sendAsMe: 'मेरे नाम से भेजें',
      dismiss: 'सुझाव हटाएँ',
      ready: 'सुझाया गया जवाब तैयार है।',
      off: 'इस ब्रांड के लिए AI सहायक बंद है, इसलिए वह जवाब नहीं सुझा सकता।',
    },
    customer: {
      title: 'ग्राहक',
      verifiedHint: 'ब्रांड की वेबसाइट पर साइन इन है; उनकी पहचान जाँची गई।',
      anonymousHint: 'साइन इन नहीं है। उनका नाम और जानकारी जाँची नहीं गई।',
      email: 'ईमेल',
      unverifiedEmail: 'दिया गया ईमेल',
      notVerified: '(सत्यापित नहीं)',
      externalId: 'ग्राहक ID',
      language: 'भाषा',
      brand: 'ब्रांड',
      since: 'ग्राहक कब से',
      lastSeen: 'आख़िरी बार देखे गए',
      previous: 'दूसरी बातचीत',
      noPrevious: 'कोई दूसरी बातचीत नहीं जिसे आप देख सकें।',
    },
    tags: {
      title: 'टैग',
      none: 'अभी कोई टैग नहीं।',
      add: 'टैग जोड़ें',
      choose: 'टैग चुनें',
      addButton: 'जोड़ें',
      remove: 'टैग {{name}} हटाएँ',
      added: 'टैग {{name}} जोड़ा गया।',
      removed: 'टैग {{name}} हटाया गया।',
      noneDefined: 'इस ब्रांड के लिए अभी कोई टैग नहीं बने हैं।',
    },
    fields: {
      title: 'कस्टम फ़ील्ड',
      none: 'इस ब्रांड के लिए कोई कस्टम फ़ील्ड नहीं बने हैं।',
      save: 'फ़ील्ड सहेजें',
      saved: 'फ़ील्ड सहेजे गए।',
    },
    links: {
      title: 'जुड़ी हुई बातचीत',
      none: 'कोई जुड़ी हुई बातचीत नहीं।',
      other: 'जोड़ने के लिए बातचीत ID',
      otherHint: 'जैसे conv_…; दोनों बातचीत अलग रहती हैं।',
      note: 'क्यों जुड़ी हैं',
      add: 'जोड़ें',
      added: 'बातचीत जोड़ी गईं।',
      remove: '{{name}} से लिंक हटाएँ',
      removed: 'लिंक हटाया गया।',
    },
    reviews: {
      title: 'सुपरवाइज़र समीक्षा',
      none: 'अभी कोई समीक्षा नहीं।',
      rating: '5 में से {{rating}}',
      ratingLabel: 'रेटिंग',
      note: 'कोचिंग नोट',
      save: 'समीक्षा सहेजें',
      saved: 'समीक्षा सहेजी गई।',
      by: '{{reviewer}} द्वारा',
      byAbout: '{{reviewer}} द्वारा, {{subject}} के बारे में',
    },
    bulk: {
      select: '{{name}} चुनें',
      selected: '{{name}} चुना गया। चुने गए: {{n}}।',
      unselected: '{{name}} का चुनाव हटाया गया। चुने गए: {{n}}।',
      count: 'चुने गए: {{n}}',
      clear: 'चुनाव हटाएँ',
      assignMe: 'मुझे सौंपें',
      resolve: 'हल करें',
      reopen: 'फिर से खोलें',
      tagLabel: 'जोड़ने के लिए टैग',
      tagPlaceholder: 'जोड़ने के लिए टैग',
      addTag: 'टैग जोड़ें',
      confirmTitle: 'चुनी गई बातचीत बदलें?',
      willChange: 'बदली जा सकती हैं: {{n}}।',
      cannotChange: 'नहीं बदली जा सकतीं: {{n}}।',
      apply: '{{n}} बदलें',
      result: 'हो गया: {{done}}। विफल: {{failed}}।',
      partial: 'हो गया: {{done}}। नहीं बदलीं: {{failed}}। सिर्फ़ उन्हीं को दोबारा आज़माया जा सकता है।',
      retryFailed: 'विफल वाली दोबारा आज़माएँ',
      failed: 'नहीं बदली',
    },
  },
  account: {
    title: 'खाते की सुरक्षा',
    description: 'आप Studio में कैसे साइन इन करते हैं।',
    twoFactor: {
      title: 'दो-चरणीय सत्यापन',
      on: 'चालू',
      off: 'बंद',
      description: 'साइन इन करते समय Studio ऑथेंटिकेटर ऐप से एक कोड मांगता है। कुछ कामों के लिए यह ज़रूरी है।',
      enable: 'चालू करें',
      disable: 'बंद करें',
      password: 'आपका पासवर्ड',
      passwordHint: 'यह सेटिंग बदलने के लिए अपना पासवर्ड डालें।',
      setupTitle: 'अपने ऑथेंटिकेटर ऐप में Hamnivo जोड़ें',
      setupBody: 'यह सेटअप कुंजी अपने ऐप में डालें, या इस डिवाइस पर लिंक खोलें।',
      secret: 'सेटअप कुंजी',
      openApp: 'ऑथेंटिकेटर ऐप में खोलें',
      backupTitle: 'अपने बैकअप कोड सेव करें',
      backupBody:
        'फ़ोन खो जाने पर हर कोड से आप एक बार साइन इन कर सकते हैं। इन्हें किसी सुरक्षित जगह रखें। ये सिर्फ़ अभी दिखाए जा रहे हैं।',
      copyCodes: 'बैकअप कोड कॉपी करें',
      verify: 'पुष्टि करें और चालू करें',
      enabled: 'दो-चरणीय सत्यापन चालू है।',
      disabled: 'दो-चरणीय सत्यापन बंद है।',
      disableBody:
        'साइन इन में सिर्फ़ आपका पासवर्ड मांगा जाएगा। जिन कामों के लिए दो-चरणीय सत्यापन ज़रूरी है, वे आपके लिए काम नहीं करेंगे।',
      wrongPassword: 'यह पासवर्ड सही नहीं है।',
    },
    passkeys: {
      title: 'पासकी',
      description: 'पासवर्ड के बजाय अपने डिवाइस के स्क्रीन लॉक से साइन इन करें।',
      add: 'पासकी जोड़ें',
      added: 'पासकी जोड़ दी गई।',
      failed: 'पासकी नहीं जुड़ी। फिर से कोशिश करें।',
      unsupported: 'यह ब्राउज़र पासकी का इस्तेमाल नहीं कर सकता।',
      none: 'अभी कोई पासकी नहीं।',
      created: 'जोड़ी गई: {{time}}',
      unnamed: 'पासकी',
      loading: 'पासकी लोड हो रही हैं',
    },
  },
  decisions: decisionsHi,
  checks: checksHi,
  caching: cachingHi,
  tools: toolsHi,
  commerce: commerceHi,
  developer: developerHi,
  reports: reportsHi,
  privacy: privacyHi,
  chatSettings: chatSettingsHi,
  chatWidget: chatWidgetHi,
  channels: channelsHi,
  automations: automationsHi,
  memory: {
    title: 'ग्राहक मेमोरी',
    description: 'तय करें कि हम आपके ग्राहकों के बारे में क्या याद रखते हैं।',
    allCustomers: 'सभी ग्राहक',
    loading: 'ग्राहक मेमोरी लोड हो रही है',
    notOffered:
      'इस ब्रांड का AI एजेंट अभी मेमोरी का इस्तेमाल नहीं करता, इसलिए यहाँ की कोई चीज़ जवाबों में इस्तेमाल नहीं होती।',
    exported: 'मेमोरी एक्सपोर्ट हो गई।',
    forbidden: {
      title: 'आप ग्राहक मेमोरी नहीं देख सकते',
      description: 'इस वर्कस्पेस के एडमिन से ग्राहक मेमोरी की अनुमति माँगें।',
    },
    list: {
      title: 'ग्राहक',
      search: 'ग्राहक खोजें',
      searchHint: 'नाम, ईमेल या ग्राहक ID।',
      searchLabel: 'ग्राहक मेमोरी में खोजें',
      submit: 'खोजें',
      brand: 'ब्रांड',
      allBrands: 'सभी ब्रांड',
      loading: 'ग्राहक लोड हो रहे हैं',
      toReview: 'समीक्षा के लिए: {{total}}',
      columns: {
        customer: 'ग्राहक',
        brand: 'ब्रांड',
        choice: 'मेमोरी',
        remembered: 'याद रखा गया',
        review: 'समीक्षा ज़रूरी',
        changed: 'आख़िरी बदलाव',
      },
      empty: {
        title: 'अभी कोई ग्राहक मेमोरी नहीं',
        description:
          'ग्राहक चैट में याद रखे जाने की सहमति देने के बाद यहाँ दिखते हैं। AI एजेंट की सेटिंग में मेमोरी चालू करें।',
        action: 'AI एजेंट खोलें',
      },
      noMatch: {
        title: 'कोई ग्राहक नहीं मिला',
        description: 'कोई दूसरा नाम, ईमेल या ग्राहक ID आज़माएँ।',
      },
    },
    customer: {
      anonymous: 'अनाम ग्राहक',
      unnamed: 'साइन-इन किया हुआ ग्राहक',
      verified: 'साइन-इन किया हुआ',
      notVerified: 'साइन-इन नहीं किया',
      since: '{{date}} से ग्राहक',
    },
    consent: {
      granted: 'मेमोरी चालू',
      declined: 'मना किया',
      withdrawn: 'मेमोरी बंद',
      undecided: 'अभी पूछा नहीं गया',
      history: 'चुनाव का इतिहास',
      by: {
        customer: 'ग्राहक द्वारा',
        staff: 'स्टाफ़ द्वारा',
      },
      line: {
        granted: '{{date}} को सहमति दी',
        declined: '{{date}} को मना किया',
        withdrawn: '{{date}} को बंद की',
        undecided: 'ग्राहक ने अभी चुनाव नहीं किया है।',
      },
    },
    status: {
      active: 'मौजूदा',
      pending: 'समीक्षा ज़रूरी',
      superseded: 'बदला गया',
      outdated: 'पुराना',
    },
    sources: {
      customer_statement: 'ग्राहक का संदेश',
      customer_control: 'ग्राहक ने सेट किया',
      staff_entry: 'स्टाफ़ ने दर्ज किया',
      agent_inference: 'अनुमान',
      system_record: 'सिस्टम रिकॉर्ड',
      import: 'इम्पोर्ट किया गया',
    },
    writers: {
      ai: 'AI सहायक',
      staff: 'स्टाफ़ सदस्य',
      customer: 'ग्राहक',
      system: 'Hamnivo',
    },
    reasons: {
      extracted_from_message: 'ग्राहक के संदेश से',
      customer_correction: 'ग्राहक ने ठीक किया',
      customer_confirmation: 'ग्राहक ने पुष्टि की',
      staff_correction: 'स्टाफ़ ने ठीक किया',
      staff_confirmation: 'स्टाफ़ ने पुष्टि की',
    },
    scope: {
      short: {
        person: 'यह ग्राहक',
        conversation: 'सिर्फ़ यह चैट',
      },
      long: {
        person: 'यह ग्राहक, इस ब्रांड की हर चैट में',
        conversation: 'सिर्फ़ वह चैट जहाँ से यह आया',
      },
    },
    slots: {
      preferred_language: {
        label: 'पसंदीदा भाषा',
        purpose: 'ग्राहक की भाषा में जवाब देना',
      },
      preferred_name: {
        label: 'पसंदीदा नाम',
        purpose: 'ग्राहक को उनके कहे अनुसार संबोधित करना',
      },
      contact_method: {
        label: 'संपर्क की पसंद',
        purpose: 'उनका पसंदीदा संपर्क तरीका इस्तेमाल करना',
      },
      contact_time: {
        label: 'संपर्क का सही समय',
        purpose: 'उनकी सुविधा के समय संपर्क करना',
      },
      communication_style: {
        label: 'जवाब का तरीका',
        purpose: 'उनकी पसंद के तरीके से जवाब देना',
      },
      delivery_note: {
        label: 'डिलीवरी नोट',
        purpose: 'आसान डिलीवरी पक्की करना',
      },
      product_interest: {
        label: 'उत्पाद में रुचि',
        purpose: 'जिसके बारे में पूछा, वह सुझाना',
      },
      size_preference: {
        label: 'साइज़',
        purpose: 'सही साइज़ सुझाना',
      },
      open_issue: {
        label: 'खुला अनुरोध',
        purpose: 'दोबारा पूछे बिना आगे बढ़ना',
      },
      stated_order_reference: {
        label: 'बताया गया ऑर्डर',
        purpose: 'उनका ऑर्डर जल्दी ढूँढना',
      },
    },
    tabs: {
      preferences: 'पसंद',
      facts: 'बातचीत की जानकारी',
      notes: 'एजेंट के नोट्स',
      internal: 'सिर्फ़ अंदरूनी',
    },
    table: {
      title: 'हम क्या याद रखते हैं',
      columns: {
        memory: 'मेमोरी',
        purpose: 'उद्देश्य',
        source: 'स्रोत',
        confirmed: 'आख़िरी पुष्टि',
        visibility: 'कौन देखता है',
      },
      noMatch: 'आपकी खोज से कुछ नहीं मिला।',
      emptyPreferences: 'कोई पसंद याद नहीं रखी गई।',
      emptyFacts: 'बातचीत की कोई जानकारी याद नहीं रखी गई।',
      searchPreferences: 'ग्राहक की पसंद खोजें…',
      searchFacts: 'बातचीत की जानकारी खोजें…',
      notes:
        'सहायक ग्राहकों के बारे में कोई नोट्स नहीं रखता। वह सिर्फ़ वही याद रखता है जो ग्राहक सहमति के बाद बताते हैं।',
    },
    panel: {
      pick: 'कोई मेमोरी कहाँ से आई, यह देखने के लिए उसे चुनें।',
      onlyApproved: 'ग्राहकों के जवाबों में सिर्फ़ स्वीकृत मान इस्तेमाल होते हैं।',
      value: 'मौजूदा मान',
      status: 'स्थिति',
      purpose: 'उद्देश्य',
      source: 'स्रोत',
      messages: 'संदेश:',
      conversation: 'बातचीत:',
      recordedBy: 'किसने दर्ज किया',
      note: 'बदलाव का कारण',
      scope: 'दायरा',
      inEffect: 'कब से लागू',
      expires: 'समाप्ति',
      noExpiry: 'कोई समाप्ति तिथि नहीं',
      verified: 'जाँच',
      notVerified: 'जाँच नहीं हुई। यह ग्राहक का कहा हुआ है।',
      version: 'वर्ज़न',
      edit: 'मान बदलें',
      newValue: 'नया मान',
      correctionReason: 'सुधार का कारण',
      valueRequired: 'नया मान लिखें।',
      valueSame: 'मौजूदा मान से अलग मान लिखें।',
      saved: 'सुधार सेव हो गया। पुराना मान इतिहास में रहता है।',
      unchanged: 'कुछ नहीं बदला: यह मान पहले से याद है।',
      history: 'इतिहास',
      historyLine: '{{writer}} · {{from}} से {{until}} तक',
    },
    reason: {
      label: 'कारण',
      hint: 'निजी जानकारी न लिखें। कारण बदलाव के साथ रखा जाता है।',
      required: 'कारण लिखें।',
    },
    actions: {
      export: 'एक्सपोर्ट',
      erase: 'पूरी मेमोरी मिटाएँ',
      outdated: 'पुराना चिह्नित करें',
      delete: 'हटाएँ',
      save: 'सुधार सेव करें',
    },
    conflict: {
      title: 'एक नया संदेश “{{label}}” को बदल सकता है',
      body: '{{date}} का एक नया संदेश है। समीक्षा करें और पुष्टि करें कि कौन-सा रखना है।',
      bodyNoCurrent: '{{date}} के एक संदेश में एक मान है जो समीक्षा का इंतज़ार कर रहा है।',
      customerCan: 'ग्राहक भी चैट में इसकी पुष्टि कर सकते हैं। तब तक कोई भी मान जवाबों में इस्तेमाल नहीं होता।',
      compare: 'तुलना करें',
      confirm: 'नए मान की पुष्टि करें',
      keep: 'मौजूदा मान रखें',
      compareTitle: 'मानों की तुलना',
      current: 'मौजूदा',
      newer: 'नया संदेश',
      none: 'कुछ नहीं',
    },
    confirmReview: {
      title: 'नए “{{label}}” की पुष्टि करें?',
      body: 'नया मान मौजूदा मान की जगह लेगा, और मौजूदा मान इतिहास में रहेगा।',
      done: 'नए मान की पुष्टि हो गई।',
    },
    reject: {
      title: 'मौजूदा मान रखें?',
      body: 'नया मान हटा दिया जाएगा और इस्तेमाल नहीं होगा। हटाने का रिकॉर्ड रखा जाएगा।',
    },
    outdated: {
      title: 'इस मेमोरी को पुराना चिह्नित करें?',
      body: 'यह अब जवाबों में इस्तेमाल नहीं होगी। हटाए जाने या समय पूरा होने तक यह इतिहास में रहेगी।',
      submit: 'पुराना चिह्नित करें',
      done: 'पुराना चिह्नित किया गया।',
    },
    delete: {
      title: 'यह मेमोरी हटाएँ?',
      intro: 'इससे यह मेमोरी इस्तेमाल से हट जाएगी:',
      body: 'यह अब ग्राहकों के जवाबों में इस्तेमाल नहीं होगी, लेकिन ऑडिट रिकॉर्ड रखा जाएगा।',
      bodyPending: 'समीक्षा के इंतज़ार वाला मान हटा दिया जाएगा। ऑडिट रिकॉर्ड रखा जाएगा।',
      submit: 'इस्तेमाल से हटाएँ',
    },
    erase: {
      title: '{{name}} के बारे में पूरी मेमोरी मिटाएँ?',
      body: 'याद रखे गए सभी मान ({{total}}) और उनका इतिहास, खोज इंडेक्स के साथ, हटा दिए जाएँगे।',
      kept: 'चैट का इतिहास, ऑडिट लॉग और हटाने का रिकॉर्ड रखे जाते हैं।',
      submit: 'पूरी मेमोरी मिटाएँ',
      turnOff: 'इस ग्राहक के लिए मेमोरी भी बंद करें',
      turnOffHint: 'ग्राहक के दोबारा सहमति देने तक कुछ नया याद नहीं रखा जाएगा।',
    },
    notice: {
      removed: 'इस्तेमाल से हटा दिया गया।',
      erased: 'इस ग्राहक की पूरी मेमोरी मिटा दी गई।',
      pending: 'जुड़े हुए इंडेक्स अपडेट हो रहे हैं। इसमें कुछ मिनट लग सकते हैं।',
      completed: 'इंडेक्स और कैश साफ़ हो गए। हटाना पूरा हुआ।',
    },
    deletions: {
      title: 'हटाए गए रिकॉर्ड और ऑडिट ट्रेल',
      description: 'क्या हटाया गया, किसने हटाया, और क्या रखा गया।',
      steps: 'हटाने के चरण',
      line: '{{who}} · {{reason}} · {{time}}',
      kept: 'रखा गया: {{items}}।',
      status: {
        pending: 'जारी है',
        completed: 'पूरा',
      },
      scope: {
        record: 'मेमोरी हटाई गई (रिकॉर्ड: {{total}})',
        customer: 'पूरी मेमोरी मिटाई गई (रिकॉर्ड: {{total}})',
      },
      requester: {
        customer: 'ग्राहक',
        staff: 'स्टाफ़ सदस्य',
        system: 'Hamnivo',
      },
      reasons: {
        customer_request: 'ग्राहक का अनुरोध',
        staff_request: 'स्टाफ़ का अनुरोध',
        erasure_request: 'मिटाने का अनुरोध',
        retention_expired: 'रखने की अवधि ख़त्म',
        review_rejected: 'समीक्षा में अस्वीकार',
      },
      stepNames: {
        records: 'रिकॉर्ड',
        embeddings: 'खोज इंडेक्स',
        citations: 'हवाले',
        caches: 'कैश',
        verification: 'जाँच',
      },
      keptNames: {
        audit_log: 'ऑडिट लॉग',
        consent_log: 'मेमोरी के चुनाव',
        deletion_record: 'हटाने का रिकॉर्ड',
        chat_history: 'चैट का इतिहास',
      },
    },
  },
  access: {
    settings: 'सेटिंग',
    you: 'आप',
    anyone: 'कोई भी',
    copyFailed: 'कॉपी नहीं हो पाया। टेक्स्ट चुनें और खुद कॉपी करें।',
    copyValue: '{{label}} कॉपी करें',
    scope: {
      tenant: 'पूरा वर्कस्पेस',
      workspace: 'वर्कस्पेस क्षेत्र: {{name}}',
      brand: 'ब्रांड: {{name}}',
      team: 'टीम: {{name}}',
      inbox: 'इनबॉक्स',
      unknown: 'वर्कस्पेस का एक हिस्सा',
    },
    scopeTypes: {
      tenant: 'पूरा वर्कस्पेस',
      workspace: 'एक वर्कस्पेस क्षेत्र',
      brand: 'एक ब्रांड',
      team: 'एक टीम',
    },
    team: {
      title: 'टीम और एक्सेस',
      description:
        'तय करें कि इस वर्कस्पेस का इस्तेमाल कौन कर सकता है, उनकी भूमिकाएं क्या हैं और वे क्या कर सकते हैं।',
      tabs: { members: 'सदस्य', roles: 'भूमिकाएं', audit: 'ऑडिट लॉग' },
      invite: 'साथी को आमंत्रित करें',
      search: 'सदस्य खोजें',
      searchPlaceholder: 'नाम या ईमेल',
      activeCount: 'सक्रिय सदस्य: {{n}}',
      loading: 'सदस्य लोड हो रहे हैं',
      noMatches: '“{{query}}” से कोई सदस्य मेल नहीं खाता।',
      clearSearch: 'खोज साफ़ करें',
      columns: {
        name: 'नाम',
        role: 'भूमिका',
        teams: 'टीमें',
        lastActive: 'पिछली बार सक्रिय',
        status: 'स्थिति',
        actions: 'कार्रवाइयां',
      },
      status: {
        active: 'सक्रिय',
        suspended: 'निलंबित',
        invited: 'आमंत्रण बाकी',
        expired: 'आमंत्रण की अवधि खत्म',
        removed: 'हटाया गया',
      },
      noTeams: 'कोई टीम नहीं',
      noRole: 'कोई भूमिका नहीं',
      notYet: 'अभी नहीं',
      invitedOn: '{{time}} को आमंत्रित',
      until: '{{time}} तक',
      actionsFor: '{{name}} के लिए कार्रवाइयां',
      changeAccess: 'भूमिका या दायरा बदलें',
      suspend: 'निलंबित करें',
      restore: 'एक्सेस वापस दें',
      remove: 'वर्कस्पेस से हटाएं',
      resend: 'आमंत्रण फिर से भेजें',
      revokeInvite: 'आमंत्रण रद्द करें',
      beyondReach: 'इनका एक्सेस आपके एक्सेस से ज़्यादा है, इसलिए आप इसे नहीं बदल सकते।',
      selfNote: 'आप यहां अपना एक्सेस नहीं बदल सकते।',
      suspendTitle: '{{name}} को निलंबित करें?',
      suspendBody:
        'इनका इस वर्कस्पेस का एक्सेस तुरंत खत्म हो जाता है, और कोई दूसरा वर्कस्पेस न होने पर ये साइन आउट हो जाते हैं। इनकी बातचीत जहां है वहीं रहती है। आप इन्हें बाद में वापस ला सकते हैं।',
      suspendSubmit: 'निलंबित करें',
      suspended: '{{name}} निलंबित हैं।',
      restoreTitle: '{{name}} को वापस लाएं?',
      restoreBody: 'इन्हें पहले वाली भूमिकाएं वापस मिलती हैं। जारी रखने के लिए ये फिर से साइन इन करते हैं।',
      restoreSubmit: 'वापस लाएं',
      restored: '{{name}} के पास फिर से एक्सेस है।',
      revokeInviteTitle: '{{email}} का आमंत्रण रद्द करें?',
      revokeInviteBody: 'इनके ईमेल वाला लिंक काम करना बंद कर देता है।',
      revokeInviteSubmit: 'आमंत्रण रद्द करें',
      inviteRevoked: '{{email}} का आमंत्रण रद्द कर दिया गया।',
      resent: '{{email}} को नया आमंत्रण लिंक भेजा गया। पुराना लिंक अब काम नहीं करता।',
      recent: 'हाल की गतिविधि',
      recentLoading: 'हाल की गतिविधि लोड हो रही है',
      recentEmpty: 'अभी तक कुछ नहीं हुआ है।',
      viewAudit: 'ऑडिट लॉग खोलें',
    },
    inviteDialog: {
      title: 'साथी को आमंत्रित करें',
      description: 'उन्हें इस वर्कस्पेस से जुड़ने के लिंक वाला ईमेल मिलता है।',
      email: 'ईमेल पता',
      emailInvalid: 'name@example.com जैसा ईमेल पता डालें।',
      role: 'भूमिका',
      roleBeyond: '{{name}} (आपके एक्सेस से बाहर)',
      where: 'भूमिका कहां लागू होती है',
      workspace: 'वर्कस्पेस क्षेत्र',
      team: 'टीम',
      brand: 'ब्रांड',
      pick: 'एक चुनें',
      pickRequired: 'चुनें कि भूमिका कहां लागू होती है।',
      expires: 'एक्सेस कब खत्म हो',
      expiresHint: 'खाली छोड़ें ताकि एक्सेस तब तक रहे जब तक कोई इसे न बदले।',
      submit: 'आमंत्रण भेजें',
      sent: '{{email}} को आमंत्रण भेजा गया। लिंक 7 दिन तक काम करता है।',
      copyLink: 'आमंत्रण लिंक कॉपी करें',
      linkHint: 'अगर इस सर्वर पर ईमेल सेट नहीं है, तो यह लिंक उन्हें खुद भेजें।',
      done: 'हो गया',
    },
    changeDialog: {
      title: '{{name}} का एक्सेस',
      description: 'हर भूमिका वहीं लागू होती है जहां दी गई थी। बदलाव उनके अगले अनुरोध पर लागू होते हैं।',
      current: 'मौजूदा भूमिकाएं',
      none: 'कोई भूमिका नहीं। ये साइन इन कर सकते हैं, पर यहां कुछ नहीं कर सकते।',
      change: 'बदलें',
      removeRole: 'भूमिका हटाएं',
      removeRoleLabel: '{{name}} से {{role}} हटाएं',
      removeRoleTitle: '{{name}} से {{role}} हटाएं?',
      removeRoleBody: 'अगले अनुरोध पर इनके पास वह नहीं रहेगा जो यह भूमिका देती है।',
      add: 'एक और भूमिका जोड़ें',
      editTitle: '{{role}} बदलें',
      addTitle: 'भूमिका जोड़ें',
      reason: 'कारण',
      reasonHint: 'ऑडिट लॉग में सेव होता है।',
      save: 'एक्सेस सेव करें',
      saved: '{{name}} का एक्सेस बदल दिया गया।',
      back: 'भूमिकाओं पर वापस जाएं',
    },
    offboard: {
      title: '{{name}} को हटाएं?',
      loading: 'जांच रहे हैं कि इनके पास क्या है',
      body: 'इनका इस वर्कस्पेस का एक्सेस तुरंत खत्म हो जाता है। इनकी भूमिकाएं हटा दी जाती हैं, ये अपनी टीमों से निकल जाते हैं और इनके पते के बाकी आमंत्रण काम करना बंद कर देते हैं। इनका पुराना काम इतिहास में बना रहता है।',
      conversations: 'इन्हें सौंपी गई खुली बातचीत: {{n}}।',
      noConversations: 'इनकी कोई खुली बातचीत नहीं है।',
      reassign: 'पहले इनकी खुली बातचीत किसी और को सौंपें',
      reassignTo: 'किसे सौंपें',
      queue: 'टीम की कतार में वापस',
      cantReassign:
        'आप बातचीत दोबारा नहीं सौंप सकते। जब तक एक्सेस वाला कोई इन्हें दोबारा न सौंपे, ये इनका इंतज़ार करती रहेंगी।',
      reason: 'कारण',
      reasonHint: 'ऑडिट लॉग में सेव होता है।',
      submit: '{{name}} को हटाएं',
      reassigning: 'बातचीत दोबारा सौंपी जा रही है',
      reassigned: 'दोबारा सौंपी गई बातचीत: {{n}}।',
      reassignFailed: 'जो बातचीत दोबारा नहीं सौंपी जा सकी: {{n}}। {{name}} को नहीं हटाया गया।',
      removed: '{{name}} को इस वर्कस्पेस से हटा दिया गया।',
      ownerTitle: 'इस मालिक को हटाने से पहले किसी और को मालिक बनाएं',
      ownerBody:
        'वर्कस्पेस में हमेशा कम से कम एक मालिक होना चाहिए। पहले किसी और को मालिक बनाएं, फिर दोबारा कोशिश करें।',
      goToMembers: 'सदस्यों पर जाएं',
    },
    roles: {
      intro: 'बिल्ट-इन भूमिकाएं आम कामों के लिए हैं। कस्टम भूमिकाएं ठीक वही अनुमतियां देती हैं जो आप चुनते हैं।',
      new: 'नई भूमिका',
      loading: 'भूमिकाएं लोड हो रही हैं',
      builtIn: 'बिल्ट-इन',
      custom: 'कस्टम',
      members: 'सदस्य: {{n}}',
      permissions: 'अनुमतियां: {{n}}',
      open: '{{name}} खोलें',
      readOnly: 'सिर्फ़ पढ़ने के लिए',
      editorNew: 'नई भूमिका',
      summary: '{{kind}} · {{members}}',
      name: 'नाम',
      key: 'कुंजी',
      keyHint: 'छोटे अंग्रेज़ी अक्षर, अंक और _, API में इस्तेमाल होती है। बाद में बदली नहीं जा सकती।',
      keyInvalid: '2–40 छोटे अक्षर, अंक या _ इस्तेमाल करें, शुरुआत अक्षर से हो।',
      nameRequired: 'नाम डालें।',
      description: 'यह भूमिका किस लिए है',
      permissionsLegend: 'अनुमतियां',
      pickOne: 'कम से कम एक अनुमति चुनें।',
      notGrantable: 'आपके एक्सेस से बाहर',
      stepUp: 'हाल ही में साइन इन ज़रूरी',
      mfa: 'दो-चरणीय सत्यापन ज़रूरी',
      presetNote:
        'बिल्ट-इन भूमिकाएं बदली नहीं जा सकतीं। किसी की कॉपी बनाकर ऐसी कस्टम भूमिका बनाएं जिसे आप बदल सकें।',
      beyondNote: 'इस भूमिका में ऐसा एक्सेस है जो आपके पास नहीं है, इसलिए आप इसे नहीं बदल सकते।',
      duplicate: 'कस्टम भूमिका के रूप में कॉपी करें',
      copyName: '{{name}} (कॉपी)',
      preview: 'सेव करने से क्या बदलेगा',
      previewLoading: 'बदलाव की जांच हो रही है',
      previewNothing: 'अभी कुछ नहीं बदलता।',
      adds: 'जोड़ता है',
      removes: 'हटाता है',
      sensitive: 'अतिरिक्त जांच ज़रूरी: {{list}}',
      outside: 'आप ये शामिल नहीं कर सकते: {{list}}',
      impact: 'किस पर असर पड़ेगा',
      impactNone: 'अभी यह भूमिका किसी के पास नहीं है।',
      gains: 'को मिलेगा: {{list}}',
      loses: 'से हटेगा: {{list}}',
      unchanged: 'कोई बदलाव नहीं',
      save: 'बदलाव सेव करें',
      create: 'भूमिका बनाएं',
      saved: 'भूमिका {{name}} सेव हो गई।',
      created: 'भूमिका {{name}} बन गई।',
      conflict:
        'आपके बदलाव करते समय किसी और ने यह भूमिका बदल दी। उनके संस्करण से तुलना करें: आपके बदलाव बने रहेंगे, और पूर्वावलोकन दिखाएगा कि सहेजने से उनके संस्करण में क्या बदलेगा।',
      reload: 'उनके संस्करण से तुलना करें',
      archive: 'भूमिका रिटायर करें',
      archiveTitle: '{{name}} को रिटायर करें?',
      archiveBody:
        'यह सूची से हट जाती है। पुराने अनुदान और ऑडिट लॉग में इसका नाम बना रहता है। सिर्फ़ वही भूमिका रिटायर हो सकती है जो किसी के पास न हो।',
      archiveSubmit: 'भूमिका रिटायर करें',
      archived: 'भूमिका {{name}} रिटायर कर दी गई।',
      heldNote: 'यह भूमिका कुछ लोगों के पास है। रिटायर करने से पहले उनका एक्सेस बदलें।',
      close: 'भूमिका बंद करें',
      discardTitle: 'इस भूमिका में किए गए बदलाव छोड़ दें?',
      discardBody: 'यहां किए गए बदलाव अभी सेव नहीं हुए हैं।',
    },
    audit: {
      intro: 'एक्सेस, सेटिंग और डेटा का हर बदलाव, क्रम से। एंट्री बदली या हटाई नहीं जा सकतीं।',
      loading: 'ऑडिट लॉग लोड हो रहा है',
      filters: 'फ़िल्टर',
      action: 'कार्रवाई',
      actionPlaceholder: 'जैसे member.removed',
      actor: 'किसने',
      target: 'लक्ष्य ID',
      targetPlaceholder: 'जैसे stf_…',
      from: 'से',
      to: 'तक',
      apply: 'फ़िल्टर लागू करें',
      clear: 'फ़िल्टर साफ़ करें',
      applied: 'दिखाया जा रहा है: {{list}}',
      empty: 'इन फ़िल्टर से कोई एंट्री मेल नहीं खाती।',
      columns: {
        time: 'समय',
        actor: 'किसने',
        action: 'क्या',
        target: 'लक्ष्य',
        outcome: 'नतीजा',
        details: 'विवरण',
      },
      outcomes: { success: 'हो गया', denied: 'मना किया गया', failed: 'विफल' },
      actorTypes: {
        staff: 'टीम सदस्य',
        service_account: 'API कुंजी',
        customer: 'ग्राहक',
        ai: 'AI',
        system: 'Hamnivo',
        platform: 'Hamnivo सहायता',
      },
      reason: 'कारण: {{reason}}',
      showData: 'दर्ज डेटा दिखाएं',
      loadMore: 'पुरानी एंट्री लोड करें',
      verify: 'अखंडता जांचें',
      verifyOk: 'ऑडिट लॉग सही है। जांची गई एंट्री: {{n}}।',
      verifyBroken: 'ऑडिट लॉग एंट्री {{seq}} पर टूटा हुआ है। यह नंबर बताकर सहायता से संपर्क करें।',
      export: 'एक्सपोर्ट करें',
      exportCsv: 'CSV के रूप में एक्सपोर्ट करें (स्प्रेडशीट)',
      exportNdjson: 'NDJSON के रूप में एक्सपोर्ट करें (हैश सहित)',
      exporting: 'एक्सपोर्ट तैयार हो रहा है',
      exported: 'एक्सपोर्ट डाउनलोड हो गया। यह ऑडिट लॉग में दर्ज है।',
      exportNote: 'एक्सपोर्ट ऊपर के फ़िल्टर के हिसाब से होते हैं और ऑडिट लॉग में दर्ज होते हैं।',
      forbiddenExport: 'एक्सपोर्ट के लिए ऑडिट एक्सपोर्ट अनुमति ज़रूरी है।',
    },
    auditActions: {
      tenant: { created: 'वर्कस्पेस बनाया गया' },
      member: {
        suspended: 'सदस्य निलंबित',
        restored: 'सदस्य वापस लाया गया',
        removed: 'सदस्य हटाया गया',
        sessions_ended: 'सेशन खत्म किए गए',
        joined_by_sso: 'सिंगल साइन-ऑन से जुड़े',
        sso_join_skipped: 'सिंगल साइन-ऑन से जुड़ना छोड़ा गया',
      },
      invitation: {
        created: 'आमंत्रण भेजा गया',
        resent: 'आमंत्रण फिर से भेजा गया',
        revoked: 'आमंत्रण रद्द किया गया',
        accepted: 'आमंत्रण स्वीकार किया गया',
      },
      grant: { created: 'भूमिका दी गई', revoked: 'भूमिका हटाई गई', replaced: 'भूमिका बदली गई' },
      role: { created: 'भूमिका बनाई गई', updated: 'भूमिका बदली गई', archived: 'भूमिका रिटायर की गई' },
      audit: { exported: 'ऑडिट लॉग एक्सपोर्ट किया गया' },
      sso: {
        domain_added: 'डोमेन जोड़ा गया',
        domain_verified: 'डोमेन सत्यापित हुआ',
        domain_check_failed: 'डोमेन की जांच विफल',
        domain_removed: 'डोमेन हटाया गया',
        connection_created: 'सिंगल साइन-ऑन जोड़ा गया',
        connection_updated: 'सिंगल साइन-ऑन बदला गया',
        connection_removed: 'सिंगल साइन-ऑन हटाया गया',
        sign_in_refused: 'सिंगल साइन-ऑन से साइन इन मना किया गया',
      },
      operator: {
        appointed: 'प्लेटफ़ॉर्म ऑपरेटर नियुक्त',
        role_changed: 'प्लेटफ़ॉर्म ऑपरेटर की भूमिका बदली',
        disabled: 'प्लेटफ़ॉर्म ऑपरेटर बंद किया गया',
      },
      incident: { declared: 'घटना घोषित की गई', resolved: 'घटना सुलझाई गई' },
      support: {
        requested: 'सहायता ने एक्सेस मांगा',
        approved: 'सहायता एक्सेस मंज़ूर',
        declined: 'सहायता एक्सेस अस्वीकार',
        revoked: 'सहायता एक्सेस रद्द',
        ended: 'सहायता एक्सेस खत्म',
        accessed: 'सहायता ने डेटा पढ़ा',
      },
    },
    security: {
      title: 'सुरक्षा',
      description: 'आपके ईमेल डोमेन के लिए सिंगल साइन-ऑन, और Hamnivo सहायता के लिए अस्थायी एक्सेस।',
      sso: {
        title: 'सिंगल साइन-ऑन (SSO)',
        description: 'आपके डोमेन के पते वाले लोग आपके पहचान प्रदाता (OpenID Connect) से साइन इन करते हैं।',
        loading: 'सिंगल साइन-ऑन लोड हो रहा है',
        forbidden: 'सिंगल साइन-ऑन वर्कस्पेस के मालिक और एडमिन संभालते हैं।',
        steps: 'साबित करें कि डोमेन आपका है, अपना पहचान प्रदाता जोड़ें, फिर इसे चालू करें।',
        domains: 'ईमेल डोमेन',
        addDomain: 'डोमेन जोड़ें',
        domain: 'डोमेन',
        domainHint: 'आपकी टीम के पतों में @ के बाद का हिस्सा, जैसे northline.example।',
        domainInvalid: 'northline.example जैसा डोमेन डालें।',
        noDomains: 'अभी कोई डोमेन नहीं।',
        verified: 'सत्यापित',
        pending: 'DNS रिकॉर्ड का इंतज़ार',
        publish:
          'यह TXT रिकॉर्ड अपने DNS प्रदाता पर प्रकाशित करें, फिर इसे जांचें। रिकॉर्ड दिखने में कुछ समय लग सकता है।',
        recordName: 'नाम',
        recordValue: 'मान',
        check: 'अभी जांचें',
        checked: 'पिछली जांच {{time}}',
        results: {
          verified: 'रिकॉर्ड मिल गया। डोमेन सत्यापित है।',
          not_found: 'रिकॉर्ड अभी नहीं मिला। नाम और मान जांचें, फिर बाद में दोबारा कोशिश करें।',
          lookup_failed: 'DNS लुकअप विफल रहा। कुछ मिनट बाद दोबारा कोशिश करें।',
          claimed_elsewhere:
            'किसी दूसरे वर्कस्पेस ने यह डोमेन पहले ही सत्यापित कर लिया है। अगर यह गलत है तो Hamnivo सहायता से संपर्क करें।',
        },
        removeDomain: 'डोमेन हटाएं',
        removeDomainLabel: '{{domain}} हटाएं',
        removeDomainTitle: '{{domain}} हटाएं?',
        removeDomainBody: 'आप इसे बाद में फिर से जोड़ सकते हैं, पर इसे दोबारा सत्यापित करना होगा।',
        connection: '{{domain}} के लिए पहचान प्रदाता',
        connectHint: 'अपने प्रदाता पर एक OpenID Connect ऐप बनाएं, फिर उसका विवरण यहां डालें।',
        issuer: 'जारीकर्ता URL (Issuer URL)',
        issuerHint: 'जैसे https://login.example.com। Hamnivo इसका डिस्कवरी दस्तावेज़ पढ़ता है।',
        issuerInvalid: 'जारीकर्ता URL डालें, जो https:// से शुरू हो।',
        clientId: 'क्लाइंट ID',
        clientSecret: 'क्लाइंट सीक्रेट',
        clientSecretHint:
          'एन्क्रिप्ट करके रखा जाता है। यह फिर कभी नहीं दिखता, सिर्फ़ इसके आखिरी अक्षर दिखते हैं।',
        required: 'इसे भरें।',
        save: 'कनेक्शन सेव करें',
        created: 'जुड़ गया। अपने प्रदाता पर रीडायरेक्ट URI दर्ज करें, फिर कनेक्शन चालू करें।',
        redirectUri: 'रीडायरेक्ट URI',
        redirectUriHint: 'यह पता अपने पहचान प्रदाता पर दर्ज करें।',
        secretMasked: 'क्लाइंट सीक्रेट {{masked}}',
        rotated: '{{time}} को बदला गया',
        replaceSecret: 'क्लाइंट सीक्रेट बदलें',
        replaceTitle: 'क्लाइंट सीक्रेट बदलें',
        replaceSubmit: 'सीक्रेट बदलें',
        replaced: 'क्लाइंट सीक्रेट बदल दिया गया।',
        endpoints: 'प्रदाता पर मिले एंडपॉइंट',
        status: { draft: 'अभी चालू नहीं', active: 'चालू', disabled: 'बंद' },
        turnOn: 'चालू करें',
        turnOff: 'बंद करें',
        turnedOn: '{{domain}} के लिए सिंगल साइन-ऑन चालू है।',
        turnedOff: '{{domain}} के लिए सिंगल साइन-ऑन बंद है। पासवर्ड फिर से काम करते हैं।',
        ssoOnly: 'सिंगल साइन-ऑन अनिवार्य करें',
        ssoOnlyHint:
          '{{domain}} के लोगों को सिंगल साइन-ऑन इस्तेमाल करना होगा। उनके लिए पासवर्ड, पासकी, साइन अप और पासवर्ड रीसेट काम नहीं करेंगे।',
        ssoOnlyNeedsOn: 'पहले कनेक्शन चालू करें।',
        ssoOnlySaved: '{{domain}} के लिए अब सिंगल साइन-ऑन अनिवार्य है।',
        ssoOnlyOff: '{{domain}} के लिए पासवर्ड और पासकी फिर से काम करते हैं।',
        jit: 'सिंगल साइन-ऑन से साइन इन करने वाले नए लोग',
        jitHint:
          'उन्हें अपने आप जोड़ने के लिए एक भूमिका चुनें, या सिर्फ़ आमंत्रित लोगों के लिए इसे बंद रखें। दो-चरणीय सत्यापन वाली भूमिकाएं नहीं चुनी जा सकतीं।',
        jitOff: 'सिर्फ़ आमंत्रित लोग',
        jitSave: 'सेव करें',
        jitSaved: 'नए लोग अब {{role}} के रूप में जुड़ते हैं।',
        jitCleared: 'अब सिर्फ़ आमंत्रित लोग जुड़ सकते हैं।',
        removeConnection: 'कनेक्शन हटाएं',
        removeConnectionTitle: '{{domain}} के लिए सिंगल साइन-ऑन हटाएं?',
        removeConnectionBody:
          '{{domain}} के लोग अब सिंगल साइन-ऑन से साइन इन नहीं कर पाएंगे। अगर उनके पास पासवर्ड था तो वे उसे रीसेट कर सकते हैं।',
        removed: '{{domain}} के लिए सिंगल साइन-ऑन हटा दिया गया।',
      },
      support: {
        title: 'सहायता एक्सेस',
        description:
          'Hamnivo सहायता आपकी मंज़ूरी के बाद ही, सीमित समय के लिए, इस वर्कस्पेस का एक हिस्सा पढ़ सकती है। एक्सेस सिर्फ़ पढ़ने के लिए है और हर बार पढ़ना आपके ऑडिट लॉग में दर्ज होता है।',
        loading: 'सहायता एक्सेस लोड हो रहा है',
        forbidden: 'सहायता एक्सेस वर्कस्पेस के मालिक मंज़ूर करते हैं।',
        pending: 'आपके फ़ैसले का इंतज़ार',
        active: 'अभी सक्रिय',
        history: 'पुराने अनुरोध',
        none: 'Hamnivo सहायता से कोई अनुरोध नहीं।',
        from: '{{name}}, Hamnivo सहायता',
        asked: '{{time}} को मांगा',
        reason: 'कारण: {{reason}}',
        areas: 'वे क्या पढ़ सकते हैं',
        minutes: 'कितनी देर के लिए',
        duration: '{{minutes}} मिनट',
        note: 'सहायता के लिए नोट',
        approve: 'मंज़ूर करें',
        decline: 'अस्वीकार करें',
        approved: 'सहायता एक्सेस {{time}} तक मंज़ूर।',
        declined: 'अनुरोध अस्वीकार कर दिया गया।',
        revoke: 'अभी रद्द करें',
        revokeTitle: 'सहायता एक्सेस अभी रद्द करें?',
        revokeBody: 'उनका अगला पढ़ना मना हो जाएगा। ज़रूरत हो तो वे फिर से मांग सकते हैं।',
        revoked: 'सहायता एक्सेस रद्द कर दिया गया।',
        endsAt: '{{time}} को खत्म',
        pickArea: 'कम से कम एक हिस्सा चुनें।',
        states: {
          pending: 'इंतज़ार में',
          active: 'सक्रिय',
          declined: 'अस्वीकार',
          revoked: 'रद्द',
          ended: 'खत्म',
          expired: 'अवधि खत्म',
        },
        scopes: {
          configuration: 'कॉन्फ़िगरेशन (सदस्य, भूमिकाएं, ब्रांड, एजेंट)',
          conversations: 'बातचीत की सूची (सिर्फ़ स्थिति, संदेश कभी नहीं)',
          audit: 'ऑडिट लॉग',
        },
      },
    },
    notices: {
      label: 'वर्कस्पेस सूचनाएं',
      supportActive: 'Hamnivo सहायता से {{name}} {{time}} तक {{areas}} पढ़ सकते हैं।',
      supportRecorded: 'हर बार पढ़ना आपके ऑडिट लॉग में दर्ज होता है।',
      review: 'देखें',
      supportPending: 'Hamnivo सहायता ने अस्थायी एक्सेस मांगा है। इंतज़ार में अनुरोध: {{n}}।',
      reviewRequest: 'अनुरोध देखें',
      incident: {
        notice: 'सूचना',
        degraded: 'सेवा धीमी',
        outage: 'सेवा बंद',
      },
      since: '{{time}} से',
      channelDown:
        '{{brand}} का {{channel}} चैनल {{time}} से काम नहीं कर रहा। नए संदेश शायद आपकी टीम तक न पहुंचें।',
      channelsDown: 'काम न कर रहे चैनल: {{n}}। नए संदेश शायद आपकी टीम तक न पहुंचें।',
      channelItem: '{{brand}} का {{channel}} चैनल, {{time}} से',
      fixChannel: 'ठीक करें',
    },
    invitation: {
      title: 'वर्कस्पेस से जुड़ें',
      description: 'आपको Hamnivo के एक वर्कस्पेस में आमंत्रित किया गया है।',
      signInFirst: 'जिस पते पर आमंत्रण भेजा गया था, उससे साइन इन करें, फिर इसे स्वीकार करें।',
      signIn: 'स्वीकार करने के लिए साइन इन करें',
      createAccount: 'खाता बनाएं',
      createHint: 'Hamnivo पर नए हैं? आमंत्रित पते से खाता बनाएं, उसकी पुष्टि करें, फिर आमंत्रण लिंक दोबारा खोलें।',
      signedInAs: '{{email}} से साइन इन',
      accept: 'आमंत्रण स्वीकार करें',
      accepted: 'आप {{name}} से जुड़ गए।',
      missing: 'इस लिंक में कोई आमंत्रण नहीं है। अपने ईमेल से लिंक दोबारा खोलें।',
      invalid:
        'यह आमंत्रण अब मान्य नहीं है। शायद इसकी अवधि खत्म हो गई, इसका इस्तेमाल हो चुका, या यह किसी दूसरे पते पर भेजा गया था। नया आमंत्रण मांगें।',
      unverified: 'पहले अपने ईमेल पते की पुष्टि करें। साइन अप करते समय हमने आपको एक लिंक भेजा था।',
      otherAccount: 'किसी दूसरे खाते से साइन इन करें',
    },
  },
  sso: {
    continue: 'सिंगल साइन-ऑन से जारी रखें',
    short: 'SSO से जारी रखें',
    email: 'काम का ईमेल पता',
    emailHint: 'हम आपको आपके संगठन के साइन-इन पेज पर भेजते हैं।',
    submit: 'जारी रखें',
    back: 'पासवर्ड से साइन इन पर वापस जाएं',
    notSetUp: 'इस पते के लिए सिंगल साइन-ऑन सेट नहीं है। अपने पासवर्ड से साइन इन करें, या अपने एडमिन से पूछें।',
    required: 'आपका संगठन इस पते के लिए सिंगल साइन-ऑन अनिवार्य करता है।',
    popupBlocked:
      'आपके ब्राउज़र ने साइन-इन विंडो रोक दी। Studio के लिए पॉप-अप की अनुमति दें, फिर दोबारा कोशिश करें।',
    failed: 'सिंगल साइन-ऑन पूरा नहीं हुआ। दोबारा कोशिश करें।',
    errors: {
      sso_email_unverified: 'आपके पहचान प्रदाता ने आपका ईमेल पता सत्यापित नहीं किया है।',
      sso_domain_mismatch: 'यह पहचान प्रदाता सिर्फ़ अपने डोमेन के पतों से साइन इन करा सकता है।',
      sso_unverified_local_account:
        'इस पते का एक Hamnivo खाता है, पर उसकी कभी पुष्टि नहीं हुई। पहले उसकी पुष्टि करें, फिर सिंगल साइन-ऑन इस्तेमाल करें।',
      sso_unavailable: 'इस पते के लिए अभी सिंगल साइन-ऑन उपलब्ध नहीं है।',
      access_denied: 'आपके पहचान प्रदाता पर साइन इन रद्द कर दिया गया।',
      generic: 'सिंगल साइन-ऑन काम नहीं किया। दोबारा कोशिश करें, या अपने एडमिन से संपर्क करें।',
    },
    done: 'साइन इन हो गया। आप यह विंडो बंद कर सकते हैं।',
  },
  reauthMore: {
    otherWays: 'या किसी दूसरे तरीके से पुष्टि करें',
    passkey: 'पासकी इस्तेमाल करें',
    sso: 'सिंगल साइन-ऑन इस्तेमाल करें',
    ssoDescription:
      'इस काम के लिए हाल में साइन इन ज़रूरी है। अपने संगठन के सिंगल साइन-ऑन से आगे बढ़ें। इस पेज पर आपका काम बना रहेगा।',
  },
  platform: {
    title: 'Hamnivo प्लेटफ़ॉर्म',
    notOperator: 'यह कंसोल Hamnivo प्लेटफ़ॉर्म ऑपरेटरों के लिए है।',
    notOperatorHint: 'आपका खाता प्लेटफ़ॉर्म ऑपरेटर नहीं है। वर्कस्पेस की भूमिकाओं से यहां एक्सेस नहीं मिलता।',
    returnToWorkspace: 'वर्कस्पेस पर लौटें',
    nav: {
      label: 'प्लेटफ़ॉर्म',
      tenants: 'टेनेंट',
      health: 'सेवाओं की स्थिति',
      support: 'सहायता एक्सेस',
      incidents: 'घटनाएं',
      audit: 'ऑडिट लॉग',
    },
    roles: {
      platform_admin: 'प्लेटफ़ॉर्म एडमिन',
      support_operator: 'सहायता ऑपरेटर',
      platform_viewer: 'प्लेटफ़ॉर्म दर्शक',
    },
    mfaNeeded: 'सहायता एक्सेस मांगने या इस्तेमाल करने के लिए खाते की सुरक्षा में दो-चरणीय सत्यापन चालू करें।',
    overview: {
      tenants: 'टेनेंट',
      active: 'सक्रिय: {{n}}',
      supportActive: 'सक्रिय सहायता एक्सेस',
      supportPending: 'फ़ैसले का इंतज़ार',
      aiStops: 'AI रोक',
      providersOff: 'बंद किए गए प्रदाता',
    },
    tenants: {
      search: 'टेनेंट खोजें',
      searchPlaceholder: 'नाम या हैंडल',
      loading: 'टेनेंट लोड हो रहे हैं',
      empty: 'कोई टेनेंट मेल नहीं खाता।',
      columns: {
        name: 'टेनेंट',
        status: 'स्थिति',
        region: 'क्षेत्र',
        aiStops: 'AI रोक',
        support: 'सहायता एक्सेस',
      },
      open: '{{name}} खोलें',
      status: { active: 'सक्रिय', suspended: 'निलंबित', closing: 'बंद हो रहा है' },
      detail: 'टेनेंट का विवरण',
      id: 'टेनेंट ID',
      created: 'बनाया गया',
      region: 'क्षेत्र',
      locale: 'डिफ़ॉल्ट भाषा',
      counts: 'वर्कस्पेस क्षेत्र: {{workspaces}} · ब्रांड: {{brands}}',
      connectors: 'कनेक्टर',
      connectorHealth: 'पिछली स्वास्थ्य जांच {{time}}',
      noConnectors: 'कोई कनेक्टर नहीं।',
      aiStops: 'AI रोक',
      noAiStops: 'AI कहीं भी रुका नहीं है।',
      aiStopScope: { tenant: 'पूरा टेनेंट', brand: 'एक ब्रांड', agent: 'एक एजेंट' },
      providers: 'मॉडल प्रदाता',
      noProviders: 'कोई प्रदाता कनेक्शन नहीं।',
      providerStatus: { active: 'चालू', disabled: 'बंद' },
      contentNote: 'प्लेटफ़ॉर्म ऑपरेटर सिर्फ़ मेटाडेटा देखते हैं। ग्राहकों की सामग्री के लिए सहायता एक्सेस ज़रूरी है।',
      noAccess: 'ग्राहकों की सामग्री का एक्सेस नहीं',
      noAccessHint:
        'पहले वर्कस्पेस को अनुरोध मंज़ूर करना होगा। एक्सेस सिर्फ़ पढ़ने के लिए और सीमित समय का होता है।',
      requestAccess: 'अस्थायी एक्सेस मांगें',
      grants: 'इस टेनेंट का सहायता एक्सेस',
    },
    request: {
      title: '{{name}} के लिए अस्थायी एक्सेस मांगें',
      description: 'वर्कस्पेस के मालिकों को ईमेल मिलता है और वे फ़ैसला करते हैं। उनकी मंज़ूरी तक कुछ साझा नहीं होता।',
      reason: 'कारण',
      reasonHint: 'बताएं कि आपको क्या जांचना है और क्यों। वर्कस्पेस यह देखता है।',
      reasonShort: 'कम से कम 10 अक्षर लिखें।',
      areas: 'आपको क्या पढ़ना है',
      minutes: 'कितनी देर',
      submit: 'अनुरोध भेजें',
      sent: 'अनुरोध भेजा गया। वर्कस्पेस के फ़ैसले के बाद यह सहायता एक्सेस में दिखेगा।',
    },
    support: {
      intro: 'टेनेंट के डेटा के लिए अस्थायी, सिर्फ़ पढ़ने वाले एक्सेस के आपके अनुरोध।',
      loading: 'सहायता एक्सेस लोड हो रहा है',
      none: 'आपने अभी तक एक्सेस नहीं मांगा है।',
      expiresIn: 'एक्सेस {{minutes}} मिनट में खत्म होगा',
      endsAt: '{{time}} को खत्म',
      end: 'सेशन खत्म करें',
      ended: 'सहायता एक्सेस खत्म हुआ।',
      approvedBy: '{{name}} ने मंज़ूर किया',
      declinedBy: '{{name}} ने अस्वीकार किया',
      note: 'नोट: {{note}}',
      requestAgain: 'फिर से मांगें',
      view: {
        configuration: 'कॉन्फ़िगरेशन देखें',
        conversations: 'बातचीत की सूची देखें',
        audit: 'ऑडिट लॉग देखें',
      },
      reading: '{{area}} लोड हो रहा है',
      members: 'सदस्य',
      roles: 'भूमिकाएं',
      brands: 'ब्रांड',
      agents: 'AI एजेंट',
      live: 'लाइव',
      notLive: 'लाइव नहीं',
      conversationsNote: 'सिर्फ़ स्थिति। संदेश सहायता के साथ कभी साझा नहीं होते।',
    },
    health: {
      checked: '{{time}} को जांचा गया',
      loading: 'सेवाएं जांची जा रही हैं',
      services: {
        api: 'API',
        database: 'डेटाबेस',
        valkey: 'कैश और कतारें',
        queue: 'बैकग्राउंड डिलीवरी',
        connectors: 'कनेक्टर',
      },
      states: { healthy: 'ठीक', degraded: 'धीमा', down: 'बंद' },
      queue: 'इंतज़ार में इवेंट: {{pending}} · विफल हो रहे: {{failing}}',
      oldest: 'सबसे पुराना इंतज़ार में: {{seconds}} से.',
      connectors: 'कनेक्टर: {{total}} · ध्यान चाहिए: {{degraded}} · बंद: {{disabled}}',
      providers: 'प्लेटफ़ॉर्म मॉडल कुंजियां',
      configured: 'सेट है',
      notConfigured: 'सेट नहीं',
    },
    incidents: {
      intro: 'घटनाएं सुलझने तक हर वर्कस्पेस में बैनर के रूप में दिखती हैं।',
      declare: 'घटना घोषित करें',
      loading: 'घटनाएं लोड हो रही हैं',
      none: 'कोई घटना नहीं।',
      active: 'सक्रिय',
      resolved: '{{time}} को सुलझी',
      started: '{{time}} को शुरू हुई',
      resolve: 'सुलझाएं',
      resolvedDone: 'घटना सुलझ गई। बैनर हट गया।',
      severity: 'गंभीरता',
      severities: { notice: 'सूचना', degraded: 'धीमा', outage: 'बंद' },
      titleField: 'शीर्षक',
      message: 'वर्कस्पेस के लिए संदेश',
      messageHint: 'बताएं कि किस पर असर है और क्या अभी भी काम करता है।',
      tooShort: 'कम से कम 3 अक्षर लिखें।',
      submit: 'घटना पोस्ट करें',
      posted: 'घटना का बैनर हर वर्कस्पेस में दिख रहा है।',
    },
    audit: {
      intro: 'इस कंसोल में किया गया हर काम, और सहायता एक्सेस पर हर वर्कस्पेस का फ़ैसला।',
      loading: 'प्लेटफ़ॉर्म ऑडिट लॉग लोड हो रहा है',
      empty: 'अभी कुछ दर्ज नहीं हुआ।',
      operator: 'ऑपरेटर',
      workspaceDecision: 'वर्कस्पेस का फ़ैसला',
      tenant: 'टेनेंट',
      loadMore: 'पुरानी एंट्री लोड करें',
    },
  },
  permissionAreas: {
    agency: 'एजेंसी',
    branding: 'ब्रांडिंग',
    conversations: 'बातचीत',
    visitors: 'विज़िटर',
    inbox: 'इनबॉक्स और रूटिंग',
    teams: 'टीमें',
    members: 'सदस्य',
    roles: 'भूमिकाएं',
    grants: 'एक्सेस',
    audit: 'ऑडिट लॉग',
    brands: 'ब्रांड',
    widget: 'चैट विजेट',
    agents: 'AI एजेंट',
    automations: 'ऑटोमेशन',
    knowledge: 'जानकारी',
    tools: 'टूल और कार्रवाइयां',
    decisions: 'फ़ैसले',
    memory: 'ग्राहक मेमोरी',
    commerce: 'ऑर्डर और कॉमर्स',
    analytics: 'रिपोर्ट',
    billing: 'बिलिंग',
    api_keys: 'API कुंजियां',
    webhooks: 'वेबहुक',
    network: 'अनुमत होस्ट',
    mcp: 'एआई सहायक (MCP)',
    integrations: 'इंटीग्रेशन',
    secrets: 'क्रेडेंशियल',
    extensions: 'एक्सटेंशन',
    migration: 'इंपोर्ट',
    settings: 'वर्कस्पेस सेटिंग',
    security: 'सुरक्षा',
    privacy: 'निजता',
    customers: 'ग्राहक डेटा',
    support: 'सहायता एक्सेस',
  },
  permissions: {
    agency: {
      read: 'एजेंसी सेटिंग और क्लाइंट निर्देशिका देखें',
      manage: 'एजेंसी सेटिंग और स्वतंत्र क्लाइंट लिंक प्रबंधित करें',
    },
    branding: {
      read: 'पोर्टल ब्रांडिंग और कस्टम डोमेन देखें',
      manage: 'पोर्टल ब्रांडिंग और सत्यापित कस्टम डोमेन प्रबंधित करें',
    },
    conversations: {
      read: 'बातचीत और ग्राहक का इतिहास पढ़ें',
      reply: 'ग्राहकों को जवाब दें',
      note: 'आंतरिक नोट लिखें',
      assign: 'बातचीत सौंपें और ट्रांसफ़र करें',
      takeover: 'AI या किसी दूसरे व्यक्ति से बातचीत अपने हाथ में लें',
      status: 'बातचीत सुलझाएं, दोबारा खोलें और स्नूज़ करें',
      tag: 'बातचीत को टैग करें और उनके कस्टम फ़ील्ड भरें',
      bulk: 'एक साथ कई बातचीत बदलें',
      review: 'दूसरों की बातचीत की समीक्षा करें और मार्गदर्शन दें',
      export: 'बातचीत के ट्रांसक्रिप्ट एक्सपोर्ट करें',
    },
    visitors: { block: 'चैट का गलत इस्तेमाल करने वाले विज़िटर को ब्लॉक और अनब्लॉक करें' },
    inbox: { manage: 'इनबॉक्स, रूटिंग और कामकाजी घंटे सेट करें' },
    teams: { manage: 'टीमें बनाएं और सदस्यता संभालें' },
    members: {
      read: 'टीम के सदस्य और उनकी भूमिकाएं देखें',
      invite: 'सदस्यों को आमंत्रित करें',
      remove: 'सदस्यों को निलंबित करें या हटाएं',
    },
    roles: { manage: 'कस्टम भूमिकाएं बनाएं और बदलें' },
    grants: { manage: 'अपने दायरे में भूमिकाएं दें और वापस लें' },
    audit: { read: 'ऑडिट लॉग पढ़ें', export: 'ऑडिट लॉग एक्सपोर्ट करें' },
    brands: { manage: 'ब्रांड बनाएं और अनुमत वेबसाइटें तय करें' },
    widget: {
      configure: 'चैट विजेट सेट करें',
      identity_keys: 'ग्राहक की पहचान सत्यापित करने वाली कुंजियां दर्ज करें',
    },
    agents: {
      read: 'AI एजेंट और उनके वर्शन देखें',
      manage: 'AI एजेंट के ड्राफ़्ट बदलें',
      publish: 'AI एजेंट प्रकाशित करें या वापस लें',
    },
    automations: {
      read: 'ऑटोमेशन और उन्होंने क्या किया, यह देखें',
      manage: 'ऑटोमेशन बनाएँ, प्रकाशित करें और बंद करें, वेबसाइट संदेशों सहित',
    },
    knowledge: { read: 'जानकारी के स्रोत पढ़ें', manage: 'जानकारी जोड़ें, बदलें और हटाएं' },
    tools: {
      read: 'टूल, कार्रवाई नीतियां, AI की कार्रवाइयां और सिमुलेशन के नतीजे देखें',
      manage: 'टूल और कार्रवाई नीतियां सेट करें',
      approve: 'AI एजेंट की सुझाई कार्रवाइयां मंज़ूर करें',
    },
    decisions: {
      read: 'रूटिंग और गुणवत्ता के फ़ैसलों की समीक्षा करें',
      manage: 'फ़ैसलों के मानदंड और सीमाएं बदलें',
    },
    memory: {
      read: 'ग्राहक मेमोरी के रिकॉर्ड पढ़ें',
      manage: 'मेमोरी रिकॉर्ड सुधारें या सीमित करें',
      delete: 'मेमोरी रिकॉर्ड हटाएं',
      erase: 'किसी ग्राहक के बारे में याद रखी हर चीज़ मिटाएं',
      export: 'किसी ग्राहक के बारे में याद रखी हर चीज़ एक्सपोर्ट करें',
    },
    commerce: {
      read: 'कार्ट, कोटेशन, ऑर्डर, भुगतान और रिफ़ंड देखें',
      carts: { manage: 'बातचीत में उत्पाद साझा करें और कोटेशन तैयार करें' },
      orders: { manage: 'ऑर्डर पर डिलीवरी के अपडेट दर्ज करें' },
      refunds: {
        request: 'ग्राहक के लिए रिफ़ंड मांगें',
        approve: 'रिफ़ंड मंज़ूर या अस्वीकार करें',
      },
      exceptions: { resolve: 'प्रदाता से जांच के बाद भुगतान और रिफ़ंड के अपवाद सुलझाएं' },
      merchants: { manage: 'व्यापारी जोड़ें और उनके क्रेडेंशियल और रिफ़ंड नियम तय करें' },
    },
    analytics: { read: 'एनालिटिक्स और रिपोर्ट देखें' },
    billing: {
      read: 'प्लान, उपयोग और इनवॉइस देखें',
      manage: 'प्लान, इनवॉइस और भुगतान संभालें',
    },
    api_keys: {
      read: 'API कुंजियां और उनका पिछला उपयोग देखें',
      manage: 'API कुंजियां बनाएं और रद्द करें',
    },
    webhooks: {
      read: 'वेबहुक और उनकी डिलीवरी लॉग देखें',
      manage: 'बाहर जाने वाले वेबहुक सेट करें',
    },
    network: { manage: 'चुनें कि Hamnivo किन बाहरी होस्ट से जुड़ सकता है' },
    mcp: {
      read: 'Claude जैसे एआई सहायकों को Hamnivo के MCP सर्वर से बातचीत, जानकारी और ग्राहक की स्मृति पढ़ने दें',
    },
    integrations: { manage: 'चैनल और थर्ड-पार्टी सेवाएं जोड़ें' },
    secrets: { manage: 'क्रेडेंशियल सहेजें और बदलें' },
    extensions: { manage: 'एक्सटेंशन इंस्टॉल करें और हटाएं' },
    migration: {
      review: 'आयात, उनकी रिपोर्ट और नमूना बातचीत देखें',
      run: 'एक्सपोर्ट फ़ाइलें अपलोड करें और आयात चलाएँ',
      manage: 'आयात को मंज़ूरी दें या वापस लें',
    },
    settings: { manage: 'वर्कस्पेस सेटिंग बदलें' },
    security: { manage: 'साइन-इन, सिंगल साइन-ऑन और सेशन के नियम सेट करें' },
    privacy: { manage: 'तय करें कि बातचीत मिटाए जाने से पहले कितने समय तक रखी जाए' },
    customers: {
      export: 'किसी एक ग्राहक की सारी जानकारी डाउनलोड करें, डेटा देखने के अनुरोध के लिए',
      erase: 'किसी एक ग्राहक को हर जगह से मिटाएँ, Chatwoot समेत, मिटाने के अनुरोध के लिए',
    },
    support: { grant: 'Hamnivo सहायता को इस वर्कस्पेस का एक्सेस दें' },
  },
};
